import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Validation from '../../../Constants/Validation';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchCouponData,
  removeCoupon,
} from '../../../redux/features/Coupon/couponSlice';
import { BASE_URL_SANDBOX, USED_LP_POINTS } from '../../../Constants/Config';
import {
  cartDepositTotal,
  depositFeeName,
  taxableDepositForCartItem,
  useContainerDeposit,
} from '../../../utils/containerDeposit';
import axios from 'axios';
import {
  getRoundValue,
  handleBogoDeals,
  handleMixAndMatchDeals,
  handleProductCategoryDiscounts,
  handleMkonnektDiscounts,
  selectIsDeziEnabled,
  zeroOrNumber,
  couponEligibleSubtotal,
  calculateCouponDiscount,
  couponDeductionPercentage,
  isApplicableCoupon,
  isCartItemCouponEligible,
  resolveDeziRcn,
} from '../../../utils';
import {
  createCheckoutStoreRequest,
  createSingleStoreRequestKey,
} from '../../../redux/features/SingleStore/singleStoresSlice';
import { createTaxDetailRequestKey } from '../../../redux/features/Payment/taxDetailSlice';
import {
  createLpscPaymentRequestKey,
  fetchLpscPaymentData,
} from '../../../redux/features/LPSC_Payment/lpscPayment';
import { createBogoRequestKey } from '../../../redux/features/Bogo/bogoListSlice';
import { createMixAndMatchRequestKey } from '../../../redux/features/MixAndMatchPricingDeals/mixAndMatchPricingDealsListSlice';
import {
  GET_DISCOUNTS_TIMEOUT_MS,
  createMkonnektDiscountRequestBody,
  createMkonnektDiscountRequestKey,
  normalizeMkonnektMerchantId,
  selectMatchingMkonnektDiscounts,
} from '../../../redux/features/MkonnektDiscount/mkonnektDiscountSlice';
import {
  clearCheckoutAdjustmentDraft,
  createCheckoutDraftScope,
  getCheckoutSessionScope,
  isCheckoutTipExplicitlyRemoved,
  patchCheckoutAdjustmentDraft,
  readCheckoutAdjustmentDraft,
} from '../../../utils/checkoutAdjustmentDraft';

/**
 * LP applies only when product.use_point is '1' and every category id in
 * product.cotegory (comma-separated) matches a store category with use_point === '1'.
 * If any category is missing or has use_point !== '1', the product is not LP-eligible.
 */
function areAllProductCategoriesLPEligible(
  product,
  storeDataState,
  categoryPointKey = 'use_point',
) {
  const categories = storeDataState?.storesData?.categories || [];
  const cotegoryRaw = product?.cotegory;
  if (cotegoryRaw == null || String(cotegoryRaw).trim() === '') {
    return true;
  }

  const categoryIds = String(cotegoryRaw)
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);

  if (categoryIds.length === 0) return true;

  return categoryIds.every((catId) => {
    const cat = categories.find((c) => String(c.id) === String(catId));
    if (!cat) return false;
    // Some payloads may not include earn_point on category.
    // In that case, fallback to use_point to keep behavior stable.
    const categoryPointValue =
      cat?.[categoryPointKey] !== undefined
        ? cat?.[categoryPointKey]
        : cat?.use_point;
    return String(categoryPointValue) === '1';
  });
}

export function isProductEligibleForLoyaltyPoints(item, storeDataState) {
  const product = item?.product;
  if (!product || product.use_point !== '1') return false;

  return areAllProductCategoriesLPEligible(
    product,
    storeDataState,
    'use_point',
  );
}

/**
 * A product can earn LP only when product.earn_point is '1' and
 * all assigned categories are LP-eligible.
 */
export function isProductEligibleForEarnLoyaltyPoints(item, storeDataState) {
  const product = item?.product;
  if (!product || product.earn_point !== '1') return false;

  return areAllProductCategoriesLPEligible(
    product,
    storeDataState,
    'earn_point',
  );
}

export function loyaltyEligibleSubtotalAfterCoupon(
  cartItems,
  storeDataState,
  couponPercentage = 0,
) {
  const parsedPercentage = parseFloat(couponPercentage);
  const normalizedPercentage = Number.isFinite(parsedPercentage)
    ? Math.min(100, Math.max(0, parsedPercentage))
    : 0;

  const total = (Array.isArray(cartItems) ? cartItems : [])
    .filter((item) =>
      isProductEligibleForLoyaltyPoints(item, storeDataState),
    )
    .reduce((sum, item) => {
      const price = parseFloat(item?.price) || 0;
      const couponAdjustedPrice =
        price - (price * normalizedPercentage) / 100;

      return sum + Math.max(0, couponAdjustedPrice);
    }, 0);

  return getRoundValue(total);
}

export function getCheckoutAdjustmentLimit(
  orderSummaryData,
  couponDiscount = 0,
  alreadyAppliedAmount = 0,
  options = {},
) {
  const toFiniteAmount = (value) => {
    const amount = Number.parseFloat(value);
    return Number.isFinite(amount) ? amount : 0;
  };
  const taxTotal = Object.values(orderSummaryData?.taxData || {}).reduce(
    (total, amount) => total + toFiniteAmount(amount),
    0,
  );
  const requestedDeliveryCharge = Number.parseFloat(options?.deliveryCharge);
  const normalizedOrderMethod = String(options?.orderMethod || '')
    .trim()
    .toLowerCase();
  const deliveryCharge =
    normalizedOrderMethod === 'pickup'
      ? 0
      : Number.isFinite(requestedDeliveryCharge)
        ? Math.max(0, requestedDeliveryCharge)
        : toFiniteAmount(orderSummaryData?.singleStoreData?.delivery_fee);
  const payableBeforeAdjustments =
    toFiniteAmount(orderSummaryData?.cartProductSum) +
    toFiniteAmount(orderSummaryData?.depositTotal) +
    taxTotal +
    toFiniteAmount(orderSummaryData?.singleStoreData?.getConvFee) +
    deliveryCharge;
  const remainingAmount =
    payableBeforeAdjustments -
    Math.max(0, toFiniteAmount(couponDiscount)) -
    Math.max(0, toFiniteAmount(alreadyAppliedAmount));

  return Math.max(0, getRoundValue(remainingAmount));
}

export function findMaximumLoyaltyAmount(maximumAmount, payableAtAmount) {
  const parsedMaximum = Number.parseFloat(maximumAmount);
  if (
    !Number.isFinite(parsedMaximum) ||
    parsedMaximum <= 0 ||
    typeof payableAtAmount !== 'function'
  ) {
    return 0;
  }

  let low = 0;
  let high = Math.max(0, Math.floor((parsedMaximum + 0.000001) * 100));

  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    const candidate = middle / 100;
    const payable = Number.parseFloat(payableAtAmount(candidate));

    if (Number.isFinite(payable) && candidate <= payable + 0.000001) {
      low = middle;
    } else {
      high = middle - 1;
    }
  }

  return low / 100;
}

export function calculateCheckoutTaxData({
  cartItems,
  taxes,
  couponPercentage = 0,
  loyaltyPercentage = 0,
  loyaltyAmount = 0,
  pennyFixLogic,
  // Optional: tax only the lines that pass this predicate. Coupon / loyalty
  // reallocation still runs over the WHOLE cart first, so each kept line is
  // taxed on exactly the price it was charged at checkout.
  lineFilter,
}) {
  let normalizedCart = Array.isArray(cartItems) ? cartItems : [];
  const normalizedTaxes = Array.isArray(taxes) ? taxes : [];
  const taxData = {};

  const parsedCouponPercentage = Number.parseFloat(couponPercentage);
  const parsedLoyaltyPercentage = Number.parseFloat(loyaltyPercentage);
  const shouldReallocateDiscounts =
    (Number.isFinite(parsedCouponPercentage) &&
      parsedCouponPercentage > 0) ||
    (Number.isFinite(parsedLoyaltyPercentage) &&
      parsedLoyaltyPercentage > 0);

  if (shouldReallocateDiscounts && typeof pennyFixLogic === 'function') {
    const discountedCart = pennyFixLogic(
      normalizedCart,
      couponPercentage,
      loyaltyPercentage,
      loyaltyAmount,
    );

    if (Array.isArray(discountedCart)) {
      normalizedCart = discountedCart;
    }
  }

  if (typeof lineFilter === 'function') {
    normalizedCart = normalizedCart.filter(lineFilter);
  }

  normalizedCart.forEach((item) => {
    normalizedTaxes.forEach((tax) => {
      if (!item?.taxesAplicable?.includes(tax?.id)) return;

      // A container deposit on the line is taxed with the line's own taxes.
      const itemPrice =
        (Number.parseFloat(item?.price) || 0) + taxableDepositForCartItem(item);
      const taxPercent = Number.parseFloat(tax?.percent) || 0;
      const rowTax = getRoundValue((itemPrice * taxPercent) / 100);
      const existingTax = Number.parseFloat(taxData[tax?.title]) || 0;
      taxData[tax?.title] = existingTax + Number.parseFloat(rowTax || 0);
    });
  });

  return Object.fromEntries(
    Object.entries(taxData).map(([title, amount]) => [
      title,
      amount ? getRoundValue(amount) : '0.00',
    ]),
  );
}

const checkoutAmountToCents = (value) => {
  const amount = Number.parseFloat(value);
  return Number.isFinite(amount)
    ? Math.round((amount + Number.EPSILON) * 100)
    : null;
};

export function checkoutTaxDataMatches(actualTaxData, expectedTaxData) {
  if (
    !actualTaxData ||
    typeof actualTaxData !== 'object' ||
    Array.isArray(actualTaxData) ||
    !expectedTaxData ||
    typeof expectedTaxData !== 'object' ||
    Array.isArray(expectedTaxData)
  ) {
    return false;
  }

  const actualKeys = Object.keys(actualTaxData).sort();
  const expectedKeys = Object.keys(expectedTaxData).sort();
  if (
    actualKeys.length !== expectedKeys.length ||
    actualKeys.some((key, index) => key !== expectedKeys[index])
  ) {
    return false;
  }

  return actualKeys.every(
    (key) =>
      checkoutAmountToCents(actualTaxData[key]) !== null &&
      checkoutAmountToCents(actualTaxData[key]) ===
        checkoutAmountToCents(expectedTaxData[key]),
  );
}

export function calculateMainCheckoutTotal(addAmount, subtractAmount) {
  const add = Number.parseFloat(addAmount);
  const subtract = Number.parseFloat(subtractAmount);

  if (!Number.isFinite(add) || !Number.isFinite(subtract)) return 0;

  const total = Number(add.toFixed(2)) - Number(subtract.toFixed(2));
  const normalizedTotal = Number(total.toFixed(2));

  return normalizedTotal > 0 ? normalizedTotal : 0;
}

export function calculateCheckoutDeductions({
  couponDiscount = 0,
  loyaltyAmount = 0,
  storeCreditAmount = 0,
} = {}) {
  return [couponDiscount, loyaltyAmount, storeCreditAmount].reduce(
    (total, value) => {
      const amount = Number.parseFloat(value);
      return total + (Number.isFinite(amount) && amount > 0 ? amount : 0);
    },
    0,
  );
}

export function evaluateCheckoutBalanceAllocation({
  ready,
  appliedLoyaltyAmount,
  appliedStoreCredit,
  availableLoyaltyAmount,
  availableStoreCredit,
  maximumLoyaltyAmount,
  payableBeforeBalances,
}) {
  if (!ready) {
    return {
      ready: false,
      valid: false,
      invalidBalance: '',
      reason: 'pricing_not_ready',
    };
  }

  const amounts = {
    appliedLoyalty: checkoutAmountToCents(appliedLoyaltyAmount),
    appliedStoreCredit: checkoutAmountToCents(appliedStoreCredit),
    availableLoyalty: checkoutAmountToCents(availableLoyaltyAmount),
    availableStoreCredit: checkoutAmountToCents(availableStoreCredit),
    maximumLoyalty: checkoutAmountToCents(maximumLoyaltyAmount),
    payableBeforeBalances: checkoutAmountToCents(payableBeforeBalances),
  };

  if (
    Object.values(amounts).some((amount) => amount === null || amount < 0)
  ) {
    return {
      ready: true,
      valid: false,
      invalidBalance: '',
      reason: 'invalid_balance_data',
    };
  }

  if (
    amounts.appliedLoyalty > amounts.availableLoyalty ||
    amounts.appliedLoyalty > amounts.maximumLoyalty
  ) {
    return {
      ready: true,
      valid: false,
      invalidBalance: 'loyalty',
      reason: 'loyalty_exceeds_limit',
    };
  }

  const storeCreditLimit = Math.max(
    0,
    amounts.payableBeforeBalances - amounts.appliedLoyalty,
  );
  if (
    amounts.appliedStoreCredit > amounts.availableStoreCredit ||
    amounts.appliedStoreCredit > storeCreditLimit
  ) {
    return {
      ready: true,
      valid: false,
      invalidBalance: 'storeCredit',
      reason: 'store_credit_exceeds_limit',
    };
  }

  return {
    ready: true,
    valid: true,
    invalidBalance: '',
    reason: '',
    remainingPayable: Math.max(
      0,
      (amounts.payableBeforeBalances -
        amounts.appliedLoyalty -
        amounts.appliedStoreCredit) /
        100,
    ),
    storeCreditLimit: storeCreditLimit / 100,
  };
}

export function createCheckoutCartRevision(cartItems) {
  return (Array.isArray(cartItems) ? cartItems : [])
    .map((item) => {
      const variants = Array.isArray(item?.varients) ? item.varients : [];
      const variantSignature = variants
        .map((variant) =>
          [
            variant?.varientName ?? variant?.variantName ?? '',
            variant?.varientVl ?? variant?.variantValue ?? variant?.value ?? '',
          ].join('='),
        )
        .join('/');
      const fallbackVariantId =
        item?.varientId ?? item?.variantId ?? item?.variant_id ?? '';
      const quantity =
        item?.quantity ?? item?.selectedQuantity ?? item?.qty ?? 1;

      return [
        item?.productId ?? item?.product_id ?? '',
        item?.categoryId ?? item?.category_id ?? '',
        variantSignature || fallbackVariantId,
        quantity,
      ]
        .map((value) => String(value ?? '').trim())
        .join(':');
    })
    .sort()
    .join('|');
}

export function isCheckoutCartLineReady(item) {
  const quantity = Number(item?.selectedQuantity);
  const price = Number(item?.price);

  return (
    Boolean(item && typeof item === 'object') &&
    Boolean(item?.product?.id ?? item?.productId ?? item?.product_id) &&
    Number.isInteger(quantity) &&
    quantity > 0 &&
    Number.isFinite(price) &&
    price >= 0 &&
    !item?.isOutOfStock &&
    !item?.isUnavailable &&
    !item?.hasHydrationError &&
    !item?.isDisabled
  );
}

const checkoutRevisionAmount = (value) => {
  const amount = Number.parseFloat(value);
  return Number.isFinite(amount) ? amount.toFixed(4) : '';
};

export function createCheckoutPricingRevision(cartItems, storeDataState) {
  return (Array.isArray(cartItems) ? cartItems : [])
    .map((item) => {
      const identity = createCheckoutCartRevision([item]);

      return [
        identity,
        checkoutRevisionAmount(item?.price),
        checkoutRevisionAmount(item?.inventoryPrice),
        checkoutRevisionAmount(item?.comparePrice),
        isCartItemCouponEligible(item) ? 'coupon:1' : 'coupon:0',
        isProductEligibleForLoyaltyPoints(item, storeDataState)
          ? 'loyalty:1'
          : 'loyalty:0',
        item?.bogoDiscountApplied ? 'bogo:1' : 'bogo:0',
        checkoutRevisionAmount(item?.bogoDiscount),
        item?.mixAndMatchDiscountApplied ? 'mix:1' : 'mix:0',
        checkoutRevisionAmount(item?.mixAndMatchDiscount),
        item?.mkonnektDiscountApplied ? 'mkonnekt:1' : 'mkonnekt:0',
        checkoutRevisionAmount(item?.mkonnektDiscount),
        checkoutRevisionAmount(item?.dealAppliedDiscount),
        checkoutRevisionAmount(item?.orderLevelDealDiscount),
      ].join(':');
    })
    .sort()
    .join('|');
}

/**
 * BOGO / mix-and-match lists must settle before checkout totals are trusted.
 * A FAILED fetch counts as settled: deals simply aren't applied. Discount
 * lookups must never block Place Order, matching mKonnekt's rule.
 */
export function isDealListReady(listState, expectedRequestKey) {
  if (!expectedRequestKey) return true;
  if (listState?.loading) return false;
  if (listState?.error) return true;
  return listState?.requestKey === expectedRequestKey;
}

/**
 * Whether the mKonnekt (dezi) quote for the current cart has settled.
 *
 * A FAILED quote for the current cart counts as settled: discount lookups must
 * never block checkout, so the cart simply proceeds with no tobacco discounts.
 * Readiness is still bound to the request key, so a stale result for a
 * different cart (or an in-flight request) keeps checkout waiting — but only
 * until `timedOutRequestKey` records that the wait for this exact request
 * expired, after which checkout proceeds without mKonnekt discounts.
 */
export function isMkonnektPricingReady({
  isDeziEnabled,
  rcn,
  mkonnektDiscounts,
  expectedRequestKey,
  timedOutRequestKey = '',
}) {
  if (!isDeziEnabled) return true;
  // A dezi merchant with no provisioned rcn never gets a quote (the sync skips
  // it), so there is nothing to wait for - without this, checkout would block
  // forever waiting on a request that is never going to be dispatched.
  if (!rcn) return true;
  // No request body (no UPC-bearing lines): nothing is quoted, nothing to wait for.
  if (!expectedRequestKey) return true;
  if (timedOutRequestKey === expectedRequestKey) return true;

  return (
    !mkonnektDiscounts?.loading &&
    mkonnektDiscounts?.requestKey === expectedRequestKey
  );
}

export function createCheckoutPricingInputRevision({
  merchantId,
  orderMethod,
  rawCart,
  variants,
  products,
  productCategoryDiscounts,
  bogoDeals,
  mixAndMatchDeals,
  mkonnektDiscounts,
  isDeziEnabled,
  storeData,
  sourceProvenance,
}) {
  try {
    return JSON.stringify([
      String(merchantId ?? '').trim().toUpperCase(),
      String(orderMethod ?? '').trim().toLowerCase(),
      rawCart || [],
      variants || [],
      products || [],
      productCategoryDiscounts || [],
      bogoDeals || [],
      mixAndMatchDeals || [],
      mkonnektDiscounts || {},
      Boolean(isDeziEnabled),
      storeData || {},
      sourceProvenance || {},
    ]);
  } catch {
    return '';
  }
}

const CouponTipSectionLogic = ({ merchantId, orderMethod }) => {
  const dispatch = useDispatch();
  const { filterCartData } = Validation();
  // Container deposit setup; null (no deposit) until it loads or when the store has none.
  const depositConfig = useContainerDeposit(merchantId);

  const cartDataState = useSelector((state) => state?.cart?.cartData);
  const storeDataState = useSelector((state) => state?.singleStores);
  const variantDataState = useSelector((state) => state.variant);
  const taxDetailState = useSelector((state) => state?.taxDetail);
  const taxDetailData = taxDetailState?.taxDetailData;
  const AuthUserState = useSelector((state) => state?.authUser?.UserData);
  const cartProductDetailsState = useSelector((state) => state.cartProducts);
  const cartProductDetails = cartProductDetailsState?.cartProductDetails;
  const lp_sc_user_data = useSelector(
    (state) => state?.lpscPayment?.LpscPaymentData,
  );
  const { productCategoryDiscounts } = useSelector(
    (state) => state.productCategoryDiscounts,
  );
  const mkonnektDiscounts = useSelector((state) => state.mkonnektDiscounts);
  const isDeziEnabled = useSelector(selectIsDeziEnabled);
  const authUserData = useSelector((state) => state.authUser);
  const lpscPaymentState = useSelector((state) => state?.lpscPayment);
  const bogoList = useSelector((state) => state.bogoList);
  const mixAndMatchList = useSelector((state) => state.mixAndMatchList);

  const [orderSummaryData, setOrderSummaryData] = useState();
  const [totalPrice, setTotalPrice] = useState();
  const singleStoreData = storeDataState?.storesData?.store_data;
  const [discountAmt, setdiscountAmt] = useState(0);
  const [percentageDeducLoy, setpercentageDeducLoy] = useState(0);
  const [percentageDeducCoupon, setpercentageDeducCoupon] = useState(0);
  const [amountDeducCoupon, setamountDeducCoupon] = useState(0);
  const [cartData, setCartAllData] = useState([]);
  // Base cart = filterCartData output only (no bogo/mix-match applied). Exposed
  // so CouponTipSection's order-placement path reuses the SAME derivation as
  // this hook instead of re-deriving its own (buildPlaceOrderData re-applies the
  // deal pipeline itself, so it needs the pre-pipeline cart).
  const [baseCartData, setBaseCartData] = useState([]);
  const [derivedPricingInputRevision, setDerivedPricingInputRevision] =
    useState('');
  const [usedLoyaltyPointApiUserData, setusedLoyaltyPointApiUserData] =
    useState('');
  const [taxValues, settaxValues] = useState(null);
  const [values, setValues] = useState({
    coupon: '',
    loyaltyPoint: '',
    tip: '',
    tipcustom: '',
    storeCredit: '',
    usedLoyaltyPoint: '',
    usedStoreCredit: '',
    tipRemoved: false,
    errors: {
      coupon: '',
      loyaltyPoint:
        parseFloat(lp_sc_user_data?.loyalty_program?.min_points_redemption) >
        parseFloat(lp_sc_user_data?.customer_lp_point?.cus_loyality_points)
          ? 'You do not have enough points to use'
          : '',
      disableLoyaltyPoint: false,
      storeCredit: '',
      tip: '',
      tipcustom: '',
    },
    cursorposition: {
      coupon: 0,
      loyaltyPoint: 0,
      tip: 0,
      tipcustom: 0,
      storeCredit: 0,
    },
  });
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const groupId = useSelector((state) => state.groupId);
  const loyaltyRequestRef = useRef(0);
  const couponRequestGenerationRef = useRef(0);
  const hydrationKeyRef = useRef('');
  const inFlightHydrationKeyRef = useRef('');
  const restoreRunRef = useRef(0);
  const currentDraftKeyRef = useRef('');
  const currentGroupIdRef = useRef('');
  const providerActiveRef = useRef(true);
  const [couponRequestPending, setCouponRequestPending] = useState(false);
  const [loyaltyRequestPending, setLoyaltyRequestPending] = useState(false);
  const [isRestoringAdjustments, setIsRestoringAdjustments] = useState(false);
  const [adjustmentsHydrated, setAdjustmentsHydrated] = useState(false);
  const [appliedTip, setAppliedTip] = useState({ mode: 'none', value: 0 });
  const [checkoutDeliveryCharge, setCheckoutDeliveryChargeState] =
    useState(null);
  const checkoutDeliveryChargeRef = useRef(null);
  const currentStoreRequestKey = createSingleStoreRequestKey(
    createCheckoutStoreRequest({
      merchant_id: merchantId,
      orderMethod,
    }),
  );
  const currentTaxRequestKey = createTaxDetailRequestKey({
    merchant_id: merchantId,
  });
  const storePricingReady =
    Boolean(singleStoreData) &&
    !storeDataState?.loading &&
    !storeDataState?.error &&
    storeDataState?.storeRequestKey === currentStoreRequestKey &&
    String(singleStoreData?.merchant_id ?? '').trim().toUpperCase() ===
      String(merchantId ?? '').trim().toUpperCase();
  const taxPricingReady =
    !taxDetailState?.loading &&
    !taxDetailState?.error &&
    Array.isArray(taxDetailData) &&
    taxDetailState?.requestKey === currentTaxRequestKey;
  const cartProductsPricingReady =
    !cartProductDetailsState?.loading &&
    !cartProductDetailsState?.error &&
    Array.isArray(cartProductDetails) &&
    String(cartProductDetailsState?.merchantId ?? '').trim().toUpperCase() ===
      String(merchantId ?? '').trim().toUpperCase();
  const variantMerchantStateKey = Object.keys(
    variantDataState?.variantCartData?.pendingByMerchant || {},
  ).find(
    (key) =>
      String(key).trim().toUpperCase() ===
      String(merchantId ?? '').trim().toUpperCase(),
  );
  const variantMerchantErrorKey = Object.keys(
    variantDataState?.variantCartData?.errorsByMerchant || {},
  ).find(
    (key) =>
      String(key).trim().toUpperCase() ===
      String(merchantId ?? '').trim().toUpperCase(),
  );
  const variantPricingReady =
    !variantMerchantStateKey && !variantMerchantErrorKey;
  const currentPromotionRequestKey = createBogoRequestKey({
    merchant_id: merchantId,
  });
  const promotionPricingReady =
    isDealListReady(bogoList, currentPromotionRequestKey) &&
    isDealListReady(
      mixAndMatchList,
      createMixAndMatchRequestKey({ merchant_id: merchantId }),
    );
  // Must derive the merchant id and rcn EXACTLY the way MkonnektDiscountSync does
  // (route merchant via normalizeMkonnektMerchantId) - this rebuilds the request
  // body only to compare its key against the one the slice recorded.
  const mkonnektMerchantId = normalizeMkonnektMerchantId(merchantId);
  const currentStoreRcn = resolveDeziRcn(singleStoreData, mkonnektMerchantId);
  const mkonnektMobileNumber = AuthUserState?.phone ?? '';
  const expectedMkonnektRequestBody = useMemo(
    () =>
      isDeziEnabled && currentStoreRcn
        ? createMkonnektDiscountRequestBody({
            cartItems: baseCartData,
            rcn: currentStoreRcn,
            mobileNumber: mkonnektMobileNumber,
            merchantId: mkonnektMerchantId,
          })
        : null,
    [
      baseCartData,
      currentStoreRcn,
      isDeziEnabled,
      mkonnektMerchantId,
      mkonnektMobileNumber,
    ],
  );
  const expectedMkonnektRequestKey = createMkonnektDiscountRequestKey(
    expectedMkonnektRequestBody,
  );
  // Key of the request whose wait expired; checkout then proceeds without
  // mKonnekt discounts instead of hanging on a quote that never matches.
  const [mkonnektTimedOutRequestKey, setMkonnektTimedOutRequestKey] =
    useState('');
  const mkonnektPricingReady = isMkonnektPricingReady({
    isDeziEnabled,
    rcn: currentStoreRcn,
    mkonnektDiscounts,
    expectedRequestKey: expectedMkonnektRequestKey,
    timedOutRequestKey: mkonnektTimedOutRequestKey,
  });

  useEffect(() => {
    if (mkonnektPricingReady || !expectedMkonnektRequestKey) return undefined;

    const timer = setTimeout(() => {
      setMkonnektTimedOutRequestKey(expectedMkonnektRequestKey);
    }, GET_DISCOUNTS_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [expectedMkonnektRequestKey, mkonnektPricingReady]);

  const setCheckoutDeliveryCharge = useCallback((value) => {
    const parsedValue = Number.parseFloat(value);
    const normalizedValue = Number.isFinite(parsedValue)
      ? Math.max(0, getRoundValue(parsedValue))
      : null;

    checkoutDeliveryChargeRef.current = normalizedValue;
    setCheckoutDeliveryChargeState(normalizedValue);
  }, []);

  useEffect(() => {
    checkoutDeliveryChargeRef.current = null;
    setCheckoutDeliveryChargeState(null);
  }, [merchantId, orderMethod]);

  const resetInMemoryCheckoutAdjustments = useCallback(
    ({ tipRemoved = false } = {}) => {
      setAppliedTip({ mode: 'none', value: 0 });
      setpercentageDeducLoy(0);
      setusedLoyaltyPointApiUserData('');
      setdiscountAmt(0);
      setValues((previousState) => ({
        ...previousState,
        coupon: '',
        loyaltyPoint: '',
        tip: '',
        tipcustom: '',
        storeCredit: '',
        usedLoyaltyPoint: '',
        usedStoreCredit: '',
        tipRemoved,
        errors: {
          ...previousState.errors,
          coupon: '',
          loyaltyPoint: '',
          storeCredit: '',
          tip: '',
          tipcustom: '',
        },
      }));
    },
    [],
  );

  const sessionScope = useMemo(
    () =>
      getCheckoutSessionScope({
        userData: AuthUserState,
        isAuthenticated: authUserData?.isAuthenticated,
        isGuest: authUserData?.isGuest,
      }),
    [
      AuthUserState,
      authUserData?.isAuthenticated,
      authUserData?.isGuest,
    ],
  );
  const checkoutDraftScope = useMemo(
    () =>
      createCheckoutDraftScope({
        merchantId,
        orderMethod,
        sessionScope,
      }),
    [merchantId, orderMethod, sessionScope],
  );
  const checkoutDraftKey = checkoutDraftScope
    ? `${checkoutDraftScope.merchantId}:${checkoutDraftScope.orderMethod}:${checkoutDraftScope.sessionScope}`
    : '';
  currentDraftKeyRef.current = checkoutDraftKey;
  currentGroupIdRef.current = String(groupId?.groupId || '');

  useEffect(() => {
    providerActiveRef.current = true;
    currentDraftKeyRef.current = checkoutDraftKey;
    currentGroupIdRef.current = String(groupId?.groupId || '');

    return () => {
      providerActiveRef.current = false;
      currentDraftKeyRef.current = '';
      currentGroupIdRef.current = '';
      couponRequestGenerationRef.current += 1;
      loyaltyRequestRef.current += 1;
      restoreRunRef.current += 1;
      hydrationKeyRef.current = '';
      inFlightHydrationKeyRef.current = '';
    };
  }, [checkoutDraftKey, groupId?.groupId]);

  const persistAdjustments = useCallback(
    (patch, options) =>
      checkoutDraftScope
        ? patchCheckoutAdjustmentDraft(checkoutDraftScope, patch, options)
        : null,
    [checkoutDraftScope],
  );

  const cartRevision = useMemo(
    () => createCheckoutCartRevision(cartDataState),
    [cartDataState],
  );
  // Identity must come from the pre-deal cart. handleBogoDeals splits qty>1
  // lines into 1-1 rows, so hashing the post-deal cart never matches the raw
  // cart and would leave checkoutPricingReady (and Place Order) stuck false.
  const derivedCartRevision = useMemo(
    () => createCheckoutCartRevision(baseCartData),
    [baseCartData],
  );
  const pricingRevision = useMemo(
    () => createCheckoutPricingRevision(cartData, storeDataState),
    [cartData, storeDataState],
  );
  const derivedCartTotal = useMemo(
    () =>
      getRoundValue(
        (Array.isArray(cartData) ? cartData : []).reduce(
          (total, item) => total + (parseFloat(item?.price) || 0),
          0,
        ),
      ),
    [cartData],
  );
  const pricingInputRevision = useMemo(
    () =>
      createCheckoutPricingInputRevision({
        merchantId,
        orderMethod,
        rawCart: cartDataState,
        variants: variantDataState?.variantCartData?.data,
        products: cartProductDetails,
        productCategoryDiscounts,
        bogoDeals: bogoList?.bogoList,
        mixAndMatchDeals: mixAndMatchList?.mixAndMatchList,
        mkonnektDiscounts,
        isDeziEnabled,
        storeData: storeDataState?.storesData,
        sourceProvenance: {
          storeRequestKey: storeDataState?.storeRequestKey,
          taxRequestKey: taxDetailState?.requestKey,
          cartProductsMerchantId: cartProductDetailsState?.merchantId,
          bogoRequestKey: bogoList?.requestKey,
          mixAndMatchRequestKey: mixAndMatchList?.requestKey,
          mkonnektRequestKey: mkonnektDiscounts?.requestKey,
        },
      }),
    [
      bogoList?.bogoList,
      bogoList?.requestKey,
      cartDataState,
      cartProductDetails,
      cartProductDetailsState?.merchantId,
      isDeziEnabled,
      merchantId,
      mixAndMatchList?.mixAndMatchList,
      mixAndMatchList?.requestKey,
      mkonnektDiscounts,
      orderMethod,
      productCategoryDiscounts,
      storeDataState?.storeRequestKey,
      storeDataState?.storesData,
      taxDetailState?.requestKey,
      variantDataState?.variantCartData?.data,
    ],
  );

  useEffect(() => {
    loyaltyRequestRef.current += 1;
    couponRequestGenerationRef.current += 1;
    restoreRunRef.current += 1;
    hydrationKeyRef.current = '';
    inFlightHydrationKeyRef.current = '';
    setCouponRequestPending(false);
    setLoyaltyRequestPending(false);
    setIsRestoringAdjustments(false);
    setAdjustmentsHydrated(false);
  }, [
    AuthUserState?.id,
    cartRevision,
    groupId?.groupId,
    merchantId,
    pricingRevision,
  ]);

  // setting cart data
  useEffect(() => {
    if (
      Array.isArray(cartDataState) &&
      Array.isArray(variantDataState?.variantCartData?.data) &&
      Array.isArray(cartProductDetails)
    ) {
      const data = filterCartData(
        cartDataState,
        variantDataState.variantCartData.data,
        cartProductDetails,
        {
          merchantId,
          hasHydrationError:
            Boolean(cartProductDetailsState?.error) ||
            Boolean(variantMerchantErrorKey),
        },
      );
      setBaseCartData(data);

      // mKonnekt (dezi) runs first so tobacco items are excluded from local deals.
      // Only a quote settled for THIS cart's request applies; otherwise (in
      // flight, timed out, or quoted for another cart) price without it.
      const mkonnektRequestBody =
        isDeziEnabled && currentStoreRcn
          ? createMkonnektDiscountRequestBody({
              cartItems: data,
              rcn: currentStoreRcn,
              mobileNumber: mkonnektMobileNumber,
              merchantId: mkonnektMerchantId,
            })
          : null;
      const afterMkonnekt = isDeziEnabled
        ? handleMkonnektDiscounts(
            data,
            selectMatchingMkonnektDiscounts(
              mkonnektDiscounts,
              mkonnektRequestBody,
            ),
          )
        : data;
      const afterProductCategoryDiscounts = handleProductCategoryDiscounts(
        afterMkonnekt,
        productCategoryDiscounts,
      );
      const afterBogoCart = handleBogoDeals(
        afterProductCategoryDiscounts,
        bogoList.bogoList,
      );
      const afterMixAndMatchCart = handleMixAndMatchDeals(
        afterBogoCart,
        mixAndMatchList.mixAndMatchList,
      );

      setCartAllData(afterMixAndMatchCart);
      setDerivedPricingInputRevision(pricingInputRevision);
    }
  }, [
    cartProductDetails,
    cartProductDetailsState?.error,
    cartDataState,
    merchantId,
    pricingInputRevision,
    productCategoryDiscounts,
    bogoList?.bogoList,
    mixAndMatchList?.mixAndMatchList,
    mkonnektDiscounts,
    isDeziEnabled,
    currentStoreRcn,
    mkonnektMerchantId,
    mkonnektMobileNumber,
    variantDataState?.variantCartData?.data,
    variantMerchantErrorKey,
  ]);

  const calculateTotalPrice = async (data) => {
    // data => if bogo list hai then after bogo cart data else cart data
    const total = await data?.reduce((acc, product) => {
      // const total = await cartData?.reduce((acc, product) => {
      return parseFloat(acc) + parseFloat(product.price);
    }, 0);

    return setTotalPrice(total.toFixed(2));
  };

  const taxCalculationLogic = (newCartItems, taxes, lpAmount) => {
    settaxValues(
      calculateCheckoutTaxData({
        cartItems: newCartItems,
        taxes,
        couponPercentage: percentageDeducCoupon,
        loyaltyPercentage: percentageDeducLoy,
        loyaltyAmount: lpAmount,
        pennyFixLogic,
      }),
    );
  };

  // calculating Tax
  useEffect(() => {
    if (cartData && taxDetailData) {
      // already getting discounted value - product, category, order level discount.. no need to again do discount calculations
      // const afterProductCategoryDiscounts = handleProductCategoryDiscounts(
      //   cartData,
      //   productCategoryDiscounts,
      // );
      // const afterBogoCart = handleBogoDeals(cartData, bogoList.bogoList);
      // const afterMixAndMatchCart = handleMixAndMatchDeals(
      //   afterBogoCart,
      //   mixAndMatchList.mixAndMatchList,
      // );

      calculateTotalPrice(cartData);
      taxCalculationLogic(cartData, taxDetailData, values.usedLoyaltyPoint);

      //-----------------------------------------------
      // NOTE: when cart data does not have product, category, order level, bogo or mix match discount use this..

      // const afterBogoCart = handleBogoDeals(cartData, bogoList.bogoList);
      // const afterMixAndMatchCart = handleMixAndMatchDeals(
      //   afterBogoCart,
      //   mixAndMatchList.mixAndMatchList,
      // );

      // calculateTotalPrice(afterMixAndMatchCart);
      // taxCalculationLogic(
      //   afterMixAndMatchCart,
      //   taxDetailData,
      //   values.usedLoyaltyPoint,
      // );
    }
  }, [
    depositConfig,
    mixAndMatchList,
    bogoList,
    cartData,
    percentageDeducLoy,
    percentageDeducCoupon,
    values.loyaltyPoint,
    values.usedLoyaltyPoint,
    taxDetailData,
  ]);

  const lpPointsCalculation = (newCartItems, taxes, lpAmount) => {
    // penny fix logic goes here...
    const couponApplied =
      percentageDeducCoupon &&
      !isNaN(percentageDeducCoupon) &&
      percentageDeducCoupon > 0;

    const loyaltyPointsApplied =
      percentageDeducLoy &&
      !isNaN(percentageDeducLoy) &&
      percentageDeducLoy > 0;

    const doPennyFix = couponApplied || loyaltyPointsApplied;

    if (doPennyFix) {
      newCartItems = pennyFixLogic(
        newCartItems,
        percentageDeducCoupon,
        percentageDeducLoy,
        lpAmount,
      );
    }

    // Track which fixed amount taxes have been applied to avoid duplicates
    // const appliedFixedTaxes = new Set();

    const newCart = newCartItems.map((item) => {
      let newItem = { ...item };

      const newItemPrice = item.pennyFixedPrice
        ? item.pennyFixedPrice
        : item.price
          ? item.price
          : 0;

      item?.taxesAplicable &&
        item?.taxesAplicable?.forEach((itemTax) => {
          const taxFound = taxes?.find((tax) => tax.id === itemTax);
          if (taxFound) {
            let taxApplied;

            // FOR FUTURE USE:
            // AS OF NOW ONLY PERCENTAGE TAXES APPLIED
            // if (taxFound.type === '1') {
            //   // Fixed amount tax - apply only once per order, not per product
            //   if (!appliedFixedTaxes.has(taxFound.id)) {
            //     taxApplied = parseFloat(taxFound.percent);
            //     appliedFixedTaxes.add(taxFound.id);
            //   } else {
            //     // Skip this tax as it's already been applied
            //     return;
            //   }
            // } else {
            //   // Percentage tax - calculate as percentage of item price
            //   taxApplied =
            //     (parseFloat(newItemPrice) * parseFloat(taxFound.percent)) / 100;
            // }

            // Percentage tax - calculate as percentage of item price
            taxApplied =
              (parseFloat(newItemPrice) * parseFloat(taxFound.percent)) / 100;

            const taxRoundedValue = getRoundValue(taxApplied);

            // incase of multiple taxes applied to the same item, add the tax amount to the total tax amount
            // else set the total tax amount to the tax rounded value
            if (newItem.totalTaxAmount) {
              newItem.totalTaxAmount =
                parseFloat(newItem.totalTaxAmount) +
                parseFloat(taxRoundedValue);
            } else {
              newItem.totalTaxAmount = parseFloat(taxRoundedValue);
            }
          }
        });

      return { ...newItem };
    });

    const getTotal = (finalizedCart, predicate) => {
      let total = finalizedCart
        .filter(predicate)
        .reduce(
          (acc, curr) =>
            (acc +=
              (doPennyFix ? +curr.pennyFixedPrice : +curr.price) +
              +(parseFloat(curr.totalTaxAmount) || 0)),
          0,
        );
      return total;
    };

    const earnPointsAmount = getTotal(newCart, (cartItem) =>
      isProductEligibleForEarnLoyaltyPoints(cartItem, storeDataState),
    );
    const nonEarnPointsAmount = getTotal(
      newCart,
      (cartItem) =>
        !isProductEligibleForEarnLoyaltyPoints(cartItem, storeDataState),
    );

    const storeCredit =
      values.usedStoreCredit && parseFloat(values.usedStoreCredit) > 0
        ? parseFloat(values.usedStoreCredit)
        : 0;

    let earnPoints = 0;

    if (storeCredit >= nonEarnPointsAmount) {
      let amountToDeduct = storeCredit - nonEarnPointsAmount;
      earnPoints = earnPointsAmount - amountToDeduct;
    } else {
      earnPoints = earnPointsAmount;
    }

    // sc - 100
    // earn amt - 82.68
    // non earn amt - 86.71

    /*
    1) sc is greater than non earn amount or not..
    2) if it is greater than.. i will have to minus from earn amount also..
    3) else earn amount will be clear as earnPointsAmount.
    5) if sc is more than non earn amt, then first minus non earn amt with SC and then again minus that with earn amount.
    */

    const usePoints = newCart
      .filter((item) => isProductEligibleForLoyaltyPoints(item, storeDataState))
      .reduce(
        (acc, curr) =>
          (acc +=
            (doPennyFix ? +curr.pennyFixedPrice : +curr.price) +
            (+curr.totalTaxAmount || 0)),
        0,
      );

    const points = {
      usePoints:
        parseFloat(usePoints) > 0 && loyaltyPointsApplied
          ? parseFloat(usePoints).toFixed(2)
          : 0,
      earnPoints:
        parseFloat(earnPoints) > 0 ? parseFloat(earnPoints).toFixed(2) : 0,
    };
    return points;
  };

  // LP calculation
  useEffect(() => {
    let points = {};
    let bogoDiscount = 0;
    let mixAndMatchDiscount = 0;
    let subtotalAfterBogo = 0;

    // const afterProductCategoryDiscounts = handleProductCategoryDiscounts(
    //   cartData,
    //   productCategoryDiscounts,
    // );
    // const afterBogoCart = handleBogoDeals(
    //   afterProductCategoryDiscounts,
    //   bogoList.bogoList,
    // );
    // const afterMixAndMatchCart = handleMixAndMatchDeals(
    //   afterBogoCart,
    //   mixAndMatchList.mixAndMatchList,
    // );

    // calculating new subtotal after bogo & mix and match discount
    const newValues = cartData.reduce(
      (acc, curr) => {
        const bogoDiscount = zeroOrNumber(curr.bogoDiscountApplied);
        const mixAndMatchDiscount = zeroOrNumber(curr.mixAndMatchDiscount);
        const mkonnektDiscount = zeroOrNumber(curr.mkonnektDiscount);

        const discount = bogoDiscount + mixAndMatchDiscount + mkonnektDiscount;
        return (acc = {
          ...acc,
          bogoDiscount: parseFloat(acc.bogoDiscount) + parseFloat(bogoDiscount),
          mixAndMatchDiscount:
            parseFloat(acc.mixAndMatchDiscount) +
            parseFloat(mixAndMatchDiscount),
          mkonnektDiscount:
            parseFloat(acc.mkonnektDiscount) + parseFloat(mkonnektDiscount),
          // curr.price already reflects the mKonnekt reduction, so the subtotal
          // stays correct without any extra subtraction below.
          total: parseFloat(acc.total) + parseFloat(curr.price),
          totalDiscount: parseFloat(acc.discount) + parseFloat(discount),
        });
      },
      {
        discount: 0,
        total: 0,
        bogoDiscount: 0,
        mixAndMatchDiscount: 0,
        mkonnektDiscount: 0,
      },
    );

    subtotalAfterBogo = parseFloat(newValues?.total).toFixed(2);
    bogoDiscount = parseFloat(newValues?.bogoDiscount).toFixed(2);
    mixAndMatchDiscount = parseFloat(newValues?.mixAndMatchDiscount).toFixed(2);
    const mkonnektDiscount = parseFloat(newValues?.mkonnektDiscount).toFixed(2);

    points = lpPointsCalculation(
      cartData,
      taxDetailData,
      usedLoyaltyPointApiUserData?.used_amt,
    );

    const dataObj = {
      cartItems: Array.isArray(cartData) && cartData.length,
      cartProductSum: parseFloat(totalPrice),
      depositTotal: cartDepositTotal(cartData, depositConfig),
      depositFeeName: depositFeeName(depositConfig),
      cartArray: cartData,
      subtotalAfterBogo: parseFloat(subtotalAfterBogo),
      couponEligibleTotal: couponEligibleSubtotal(cartData),
      singleStoreData: singleStoreData,
      taxData: taxValues,
      taxDetailApiData: taxDetailData,
      bogoDiscount,
      mixAndMatchDiscount,
      mkonnektDiscount,
      lp_sc_data: {
        enable_loyalty: lp_sc_user_data?.loyalty_program?.enable_loyalty,
        store_credit_amt: values.usedStoreCredit,
        spend_loyality_amount: usedLoyaltyPointApiUserData?.used_amt,
        spend_loyality_points: usedLoyaltyPointApiUserData?.used_points,
        total_lp_apply_amt: points?.usePoints,
        total_lp_earn_amt: points?.earnPoints,
      },
      discountAmt: discountAmt,
      percentageDeducCoupon: percentageDeducCoupon ?? 0,
      percentageDeducLoy: percentageDeducLoy ?? 0,
      cartRevision: derivedCartRevision,
      pricingRevision,
      pricingInputRevision: derivedPricingInputRevision,
    };

    setOrderSummaryData(dataObj);
  }, [
    depositConfig,
    cartData,
    derivedCartRevision,
    derivedPricingInputRevision,
    pricingRevision,
    pricingInputRevision,
    totalPrice,
    singleStoreData,
    taxValues,
    percentageDeducLoy,
    values.usedStoreCredit,
    values.loyaltyPoint,
    discountAmt,
    // additional dependencies....
    amountDeducCoupon,
    lp_sc_user_data?.loyalty_program?.enable_loyalty,
    percentageDeducCoupon,
    taxDetailData,
    usedLoyaltyPointApiUserData?.used_amt,
    usedLoyaltyPointApiUserData?.used_points,
    bogoList.bogoList,
    mixAndMatchList.mixAndMatchList,
    productCategoryDiscounts,
  ]);

  const { validateCoupon, getCurrentDateTime, pennyFixLogic } = Validation();

  const getCouponAdjustedLoyaltyBase = (couponPercentage) => {
    return loyaltyEligibleSubtotalAfterCoupon(
      cartData,
      storeDataState,
      couponPercentage,
    );
  };

  const getProjectedOrderSummary = (
    loyaltyAmount,
    couponPercentage,
    summary = orderSummaryData,
  ) => {
    const normalizedLoyaltyAmount = Math.max(
      0,
      Number.parseFloat(loyaltyAmount) || 0,
    );
    const eligibleBase = getCouponAdjustedLoyaltyBase(couponPercentage);
    const loyaltyPercentage =
      eligibleBase > 0
        ? (normalizedLoyaltyAmount / eligibleBase) * 100
        : 0;
    const projectedTaxData = calculateCheckoutTaxData({
      cartItems: cartData,
      taxes: taxDetailData,
      couponPercentage,
      loyaltyPercentage,
      loyaltyAmount: normalizedLoyaltyAmount,
      pennyFixLogic,
    });

    return {
      ...summary,
      taxData: projectedTaxData,
    };
  };

  const getPayableForLoyaltyAmount = (
    loyaltyAmount,
    couponDiscount,
    couponPercentage,
    summary = orderSummaryData,
  ) =>
    getCheckoutAdjustmentLimit(
      getProjectedOrderSummary(
        loyaltyAmount,
        couponPercentage,
        summary,
      ),
      couponDiscount,
      0,
      {
        deliveryCharge: checkoutDeliveryChargeRef.current,
        orderMethod,
      },
    );

  const getMaximumSafeLoyaltyAmount = (
    upperBound,
    couponDiscount,
    couponPercentage,
    summary = orderSummaryData,
  ) =>
    findMaximumLoyaltyAmount(upperBound, (candidate) =>
      getPayableForLoyaltyAmount(
        candidate,
        couponDiscount,
        couponPercentage,
        summary,
      ),
    );

  const getStoreCreditLimitAfterLoyalty = (
    loyaltyAmount,
    couponDiscount,
    couponPercentage,
    summary = orderSummaryData,
  ) =>
    getCheckoutAdjustmentLimit(
      getProjectedOrderSummary(
        loyaltyAmount,
        couponPercentage,
        summary,
      ),
      couponDiscount,
      loyaltyAmount,
      {
        deliveryCharge: checkoutDeliveryChargeRef.current,
        orderMethod,
      },
    );

  const validateCheckoutBalanceAllocation = (options = {}) => {
    const appliedLoyaltyAmount = Math.max(
      0,
      Number.parseFloat(valuesRef.current.usedLoyaltyPoint) || 0,
    );
    const appliedStoreCredit = Math.max(
      0,
      Number.parseFloat(valuesRef.current.usedStoreCredit) || 0,
    );
    const requestedDeliveryCharge = Number.parseFloat(options.deliveryCharge);
    const hasRequestedDeliveryCharge = Number.isFinite(
      requestedDeliveryCharge,
    );
    const activeDeliveryCharge = hasRequestedDeliveryCharge
      ? Math.max(0, getRoundValue(requestedDeliveryCharge))
      : checkoutDeliveryChargeRef.current;
    const storedDeliveryCharge = checkoutDeliveryChargeRef.current;
    const deliveryChargeReady =
      Number.isFinite(activeDeliveryCharge) &&
      Number.isFinite(storedDeliveryCharge) &&
      (!hasRequestedDeliveryCharge ||
        checkoutAmountToCents(activeDeliveryCharge) ===
          checkoutAmountToCents(storedDeliveryCharge));
    const orderSummaryReady =
      storePricingReady &&
      cartProductsPricingReady &&
      variantPricingReady &&
      promotionPricingReady &&
      mkonnektPricingReady &&
      Array.isArray(cartData) &&
      cartData.length > 0 &&
      cartData.every(isCheckoutCartLineReady) &&
      derivedCartRevision === cartRevision &&
      orderSummaryData &&
      Number.isFinite(Number.parseFloat(orderSummaryData.cartProductSum)) &&
      checkoutAmountToCents(orderSummaryData.cartProductSum) ===
        checkoutAmountToCents(derivedCartTotal) &&
      orderSummaryData.singleStoreData &&
      orderSummaryData.cartRevision === derivedCartRevision &&
      orderSummaryData.pricingRevision === pricingRevision &&
      derivedPricingInputRevision === pricingInputRevision &&
      orderSummaryData.pricingInputRevision === pricingInputRevision &&
      String(orderSummaryData.singleStoreData?.merchant_id ?? '')
        .trim()
        .toUpperCase() === String(merchantId ?? '').trim().toUpperCase();
    const projectedSummary = orderSummaryReady
      ? getProjectedOrderSummary(
          appliedLoyaltyAmount,
          percentageDeducCoupon,
          orderSummaryData,
        )
      : null;
    const projectedTaxData = projectedSummary?.taxData;
    const summaryTaxReady = checkoutTaxDataMatches(
      orderSummaryData?.taxData,
      projectedTaxData,
    );
    const projectedTaxTotal = Object.values(projectedTaxData || {}).reduce(
      (total, amount) => total + (Number.parseFloat(amount) || 0),
      0,
    );
    const expectedTaxProvided = Object.prototype.hasOwnProperty.call(
      options,
      'expectedTax',
    );
    const expectedCouponProvided = Object.prototype.hasOwnProperty.call(
      options,
      'expectedCouponDiscount',
    );
    const expectedGrandTotalProvided = Object.prototype.hasOwnProperty.call(
      options,
      'expectedGrandTotal',
    );
    const expectedTaxReady =
      !expectedTaxProvided ||
      (checkoutAmountToCents(options.expectedTax) !== null &&
        checkoutAmountToCents(options.expectedTax) ===
          checkoutAmountToCents(projectedTaxTotal));
    const expectedCouponReady =
      !expectedCouponProvided ||
      (checkoutAmountToCents(options.expectedCouponDiscount) !== null &&
        checkoutAmountToCents(options.expectedCouponDiscount) ===
          checkoutAmountToCents(amountDeducCoupon));
    const hasAppliedBalance =
      appliedLoyaltyAmount > 0 || appliedStoreCredit > 0;
    const availableLoyaltyValue = Number.parseFloat(
      lp_sc_user_data?.customer_lp_point?.cus_loyality_points_amount,
    );
    const availableStoreCreditValue = Number.parseFloat(
      lp_sc_user_data?.customer_store_credit,
    );
    const availableLoyaltyAmount = Number.isFinite(availableLoyaltyValue)
      ? Math.max(0, availableLoyaltyValue)
      : appliedLoyaltyAmount > 0
        ? Number.NaN
        : 0;
    const availableStoreCredit = Number.isFinite(availableStoreCreditValue)
      ? Math.max(0, availableStoreCreditValue)
      : appliedStoreCredit > 0
        ? Number.NaN
        : 0;
    const balanceDataReady =
      !hasAppliedBalance ||
      (!lpscPaymentState?.loading &&
        !lpscPaymentState?.error &&
        lpscPaymentState?.requestKey ===
          createLpscPaymentRequestKey({
            customer_id: AuthUserState?.id,
            merchant_id: merchantId,
            group_id: currentGroupIdRef.current,
          }) &&
        Number.isFinite(availableLoyaltyAmount) &&
        Number.isFinite(availableStoreCredit));
    const summaryLoyaltyAmount =
      checkoutAmountToCents(
        orderSummaryData?.lp_sc_data?.spend_loyality_amount,
      ) ?? 0;
    const summaryStoreCredit =
      checkoutAmountToCents(orderSummaryData?.lp_sc_data?.store_credit_amt) ??
      0;
    const summaryDiscountAmount =
      checkoutAmountToCents(orderSummaryData?.discountAmt) ?? 0;
    const adjustmentSummaryReady =
      summaryLoyaltyAmount === checkoutAmountToCents(appliedLoyaltyAmount) &&
      summaryStoreCredit === checkoutAmountToCents(appliedStoreCredit) &&
      summaryDiscountAmount === checkoutAmountToCents(appliedLoyaltyAmount);
    const baseReady =
      adjustmentsHydrated &&
      !isRestoringAdjustments &&
      !couponRequestPending &&
      !loyaltyRequestPending &&
      taxPricingReady &&
      storePricingReady &&
      cartProductsPricingReady &&
      variantPricingReady &&
      promotionPricingReady &&
      mkonnektPricingReady &&
      deliveryChargeReady &&
      orderSummaryReady &&
      summaryTaxReady &&
      expectedTaxReady &&
      expectedCouponReady &&
      balanceDataReady &&
      adjustmentSummaryReady;
    const loyaltyEligibleAmount = baseReady
      ? getCouponAdjustedLoyaltyBase(percentageDeducCoupon)
      : 0;
    const maximumLoyaltyAmount = baseReady
      ? getMaximumSafeLoyaltyAmount(
          Math.min(loyaltyEligibleAmount, availableLoyaltyAmount),
          amountDeducCoupon,
          percentageDeducCoupon,
          projectedSummary,
        )
      : 0;
    const payableBeforeBalances = baseReady
      ? getCheckoutAdjustmentLimit(projectedSummary, amountDeducCoupon, 0, {
          deliveryCharge: activeDeliveryCharge,
          orderMethod,
        })
      : 0;
    const evaluation = evaluateCheckoutBalanceAllocation({
      ready: baseReady,
      appliedLoyaltyAmount,
      appliedStoreCredit,
      availableLoyaltyAmount,
      availableStoreCredit,
      maximumLoyaltyAmount,
      payableBeforeBalances,
    });
    const tipAmount = Math.max(0, Number.parseFloat(options.tipAmount) || 0);
    const canonicalGrandTotal = evaluation.valid
      ? calculateMainCheckoutTotal(
          payableBeforeBalances + amountDeducCoupon + tipAmount,
          amountDeducCoupon + appliedLoyaltyAmount + appliedStoreCredit,
        )
      : null;
    const taxRevision = Object.keys(projectedTaxData || {})
      .sort()
      .map(
        (title) =>
          `${title}:${checkoutAmountToCents(projectedTaxData[title]) ?? ''}`,
      )
      .join(',');
    const revisionKey = evaluation.valid
      ? JSON.stringify([
          checkoutDraftKey,
          cartRevision,
          pricingRevision,
          taxRevision,
          checkoutAmountToCents(activeDeliveryCharge),
          checkoutAmountToCents(amountDeducCoupon),
          Number.parseFloat(percentageDeducCoupon) || 0,
          couponData?.id ?? couponData?.coupon_id ?? '',
          couponData?.coupon_code ?? couponData?.couponCode ?? '',
          couponData?.name ?? '',
          checkoutAmountToCents(appliedLoyaltyAmount),
          String(usedLoyaltyPointApiUserData?.used_points ?? ''),
          checkoutAmountToCents(appliedStoreCredit),
          checkoutAmountToCents(tipAmount),
          checkoutAmountToCents(canonicalGrandTotal),
          orderMethod,
          currentGroupIdRef.current,
        ])
      : '';
    const expectedGrandTotalReady =
      !expectedGrandTotalProvided ||
      (checkoutAmountToCents(options.expectedGrandTotal) !== null &&
        checkoutAmountToCents(options.expectedGrandTotal) ===
          checkoutAmountToCents(canonicalGrandTotal));

    if (!expectedGrandTotalReady) {
      return {
        ...evaluation,
        ready: false,
        valid: false,
        invalidBalance: '',
        reason: 'pricing_not_reconciled',
        canonicalGrandTotal,
        revisionKey,
      };
    }

    return {
      ...evaluation,
      canonicalGrandTotal,
      maximumLoyaltyAmount,
      payableBeforeBalances,
      projectedTaxData,
      revisionKey,
    };
  };

  const [couponData, setcouponData] = useState('');
  const customerId = useMemo(() => {
    return authUserData.isAuthenticated &&
      authUserData.UserData &&
      authUserData.UserData.id
      ? authUserData.UserData.id
      : '';
  }, [authUserData]);

  const clearDerivedCoupon = useCallback(() => {
    setpercentageDeducCoupon(0);
    setamountDeducCoupon(0);
    setcouponData('');
  }, []);

  const requestCoupon = useCallback(
    async (rawCouponCode, options = {}) => {
      const couponCode = String(rawCouponCode || '').trim();
      const requestDraftKey = checkoutDraftKey;
      if (!couponCode || !requestDraftKey) return null;

      const generation = ++couponRequestGenerationRef.current;
      const shouldPersist = options.persist !== false;
      const refreshExpiry = options.refreshExpiry !== false;
      const requestGroupId = String(groupId?.groupId || '');
      const data = {
        coupon: couponCode,
        merchant_id: merchantId,
        current_date_time: getCurrentDateTime(),
        customer_id: customerId,
        group_id: requestGroupId,
      };

      setCouponRequestPending(true);

      try {
        const thunkPromise = dispatch(fetchCouponData(data));
        const requestId = thunkPromise.requestId;
        const action = await thunkPromise;
        const requestStillCurrent =
          providerActiveRef.current &&
          generation === couponRequestGenerationRef.current &&
          requestDraftKey === currentDraftKeyRef.current &&
          requestId === action?.meta?.requestId &&
          String(action?.meta?.arg?.merchant_id || '') === String(merchantId) &&
          String(action?.meta?.arg?.group_id || '') === requestGroupId &&
          currentGroupIdRef.current === requestGroupId;

        if (!requestStillCurrent) return null;

        const requestSucceeded = fetchCouponData.fulfilled.match(action);
        if (!requestSucceeded) {
          setValues((previousState) => ({
            ...previousState,
            coupon: shouldPersist ? couponCode : '',
            errors: {
              ...previousState.errors,
              coupon:
                action?.error?.message ||
                'Unable to validate this coupon. Please try again.',
            },
          }));
          return null;
        }

        const responseMessage = action.payload?.message || '';
        const coupon = action.payload?.result;
        const minimumAmount = parseFloat(coupon?.min_amount);
        const eligibleBase = couponEligibleSubtotal(cartData);

        let errorMessage = responseMessage;
        if (
          !errorMessage &&
          isApplicableCoupon(coupon) &&
          eligibleBase <= 0
        ) {
          errorMessage = "This coupon can't be applied to the items in your cart.";
        } else if (
          !errorMessage &&
          Number.isFinite(minimumAmount) &&
          minimumAmount > eligibleBase
        ) {
          errorMessage = `Minimum order amount should be $${minimumAmount.toFixed(
            2,
          )}`;
        } else if (!errorMessage && !isApplicableCoupon(coupon)) {
          errorMessage = 'Unable to apply this coupon.';
        }

        if (errorMessage) {
          clearDerivedCoupon();
          persistAdjustments(
            { couponCode: '' },
            { refreshExpiry },
          );
          setValues((previousState) => ({
            ...previousState,
            coupon: '',
            errors: {
              ...previousState.errors,
              coupon: errorMessage,
            },
          }));
          return null;
        }

        setpercentageDeducCoupon(
          couponDeductionPercentage(coupon, eligibleBase),
        );
        setamountDeducCoupon(calculateCouponDiscount(coupon, eligibleBase));
        setcouponData(coupon);
        setValues((previousState) => ({
          ...previousState,
          coupon: '',
          errors: {
            ...previousState.errors,
            coupon: '',
          },
        }));

        if (shouldPersist) {
          persistAdjustments(
            { couponCode: coupon?.name || couponCode },
            { refreshExpiry },
          );
        }

        return coupon;
      } finally {
        if (generation === couponRequestGenerationRef.current) {
          setCouponRequestPending(false);
        }
      }
    },
    [
      cartData,
      checkoutDraftKey,
      clearDerivedCoupon,
      customerId,
      dispatch,
      getCurrentDateTime,
      groupId?.groupId,
      merchantId,
      persistAdjustments,
    ],
  );
  const handleApplyCoupon = (e) => {
    if (values.usedLoyaltyPoint !== '') {
      setValues((previousState) => ({
        ...previousState,
        errors: {
          ...previousState.errors,
          coupon: 'Please remove loyalty points for using a coupon code',
        },
      }));
    }

    if (values.usedStoreCredit !== '') {
      setValues((previousState) => ({
        ...previousState,
        errors: {
          coupon: 'Please remove store credit for using a coupon code',
        },
      }));
    }

    if (values.usedLoyaltyPoint !== '' && values.usedStoreCredit !== '') {
      setValues((previousState) => ({
        ...previousState,
        errors: {
          ...previousState.errors,
          coupon:
            'Please remove loyalty point and store credit points to add coupon code',
          storeCredit: '',
        },
      }));
    }

    if (values.usedLoyaltyPoint === '' && values.usedStoreCredit === '') {
      if (values.errors.coupon === '') {
        void requestCoupon(values.coupon);
      }
    }
  };

  const handleRemoveCoupon = () => {
    couponRequestGenerationRef.current += 1;
    setCouponRequestPending(false);
    dispatch(removeCoupon(''));
    clearDerivedCoupon();
    setValues((previousState) => ({
      ...previousState,
      coupon: '',
    }));
    persistAdjustments({ couponCode: '' });
  };

  const handleAddingTip = useCallback(
    (mytip) => {
      if (mytip === '' || mytip === null) {
        setAppliedTip({ mode: 'none', value: 0 });
        setValues((previousState) => ({
          ...previousState,
          tip: '',
          tipcustom: '',
          tipRemoved: true,
        }));
        persistAdjustments({ tip: { mode: 'none', value: 0 } });
        return;
      }

      if (mytip === 'Custom') {
        setAppliedTip({ mode: 'none', value: 0 });
        setValues((previousState) => ({
          ...previousState,
          tip: 'Custom',
          tipRemoved: false,
        }));
        persistAdjustments({ tip: { mode: 'none', value: 0 } });
        return;
      }

      const percentage = parseFloat(mytip);
      if (!Number.isFinite(percentage) || percentage <= 0) return;

      setAppliedTip({ mode: 'percent', value: percentage });
      setValues((previousState) => ({
        ...previousState,
        tip: String(percentage),
        tipcustom: '',
        tipRemoved: false,
      }));
      persistAdjustments({
        tip: { mode: 'percent', value: percentage },
      });
    },
    [persistAdjustments],
  );

  const persistCustomTip = (amount) => {
    const customAmount = parseFloat(amount);
    if (!Number.isFinite(customAmount) || customAmount <= 0) return false;

    const normalizedAmount = customAmount.toFixed(2);
    setAppliedTip({ mode: 'custom', value: customAmount });
    setValues((previousState) => ({
      ...previousState,
      tip: '',
      tipcustom: normalizedAmount,
      tipRemoved: false,
      errors: {
        ...previousState.errors,
        tipcustom: '',
      },
    }));
    persistAdjustments({
      tip: { mode: 'custom', value: customAmount },
    });
    return true;
  };

  const clearPersistedTip = useCallback(() => {
    setAppliedTip({ mode: 'none', value: 0 });
    return persistAdjustments({ tip: { mode: 'none', value: 0 } });
  }, [persistAdjustments]);

  const clearTipAdjustment = useCallback(() => {
    setValues((previousState) => ({
      ...previousState,
      tip: '',
      tipcustom: '',
      tipRemoved: true,
    }));
    clearPersistedTip();
  }, [clearPersistedTip]);

  const handleInputChange = (event) => {
    event.preventDefault();
    const { errors, cursorposition } = values;

    const fieldname = event.target.name;
    let fieldValue =
      fieldname === 'coupon'
        ? event.target.value.toUpperCase()
        : fieldname === 'tipcustom'
          ? event.target.value
              .replace(/[^\d.]/g, '')
              .replace(/^(\d*\.)(.*)\./, '$1$2')
              .replace(/^(\d*\.\d*)(.*)\./, '$1$2')
          : event.target.value;

    const newCursorPosition = event.target.selectionStart;

    switch (fieldname) {
      case 'coupon':
        fieldValue.toUpperCase();
        cursorposition.coupon = newCursorPosition;
        validateCoupon(fieldValue, errors);
        break;
      case 'tip':
        cursorposition.coupon = newCursorPosition;
        validateCoupon(fieldValue, errors);
        break;
      case 'tipcustom':
        if (values.tipcustom === '') {
          values.errors.tipcustom = 'Please enter tip amount';
        } else {
          let inputStr = fieldValue.replace(/\D/g, ''); // Remove all non-digit characters for clean processing

          if (inputStr === '') {
            fieldValue = '0.00'; // Reset to default value if input is empty
          } else {
            // Remove leading zeros for clean numeric string
            inputStr = inputStr.replace(/^0+/, '');

            if (inputStr.length === 0) {
              fieldValue = '0.00';
            } else if (inputStr.length === 1) {
              fieldValue = '0.0' + inputStr;
            } else if (inputStr.length === 2) {
              fieldValue = '0.' + inputStr;
            } else {
              fieldValue =
                inputStr.slice(0, inputStr.length - 2) +
                '.' +
                inputStr.slice(-2);
            }
          }
        }
        cursorposition.tipcustom = event.target.selectionStart;
        break;

      default:
        break;
    }

    setValues((previousState) => ({
      errors,
      ...previousState,
      [fieldname]: fieldValue,
    }));
  };

  const handleStoreCredit = (event, grandtotal, storecredit, mytipamt) => {
    event.preventDefault();

    const { errors, cursorposition } = values;

    const fieldname = event.target.name;
    let fieldValue =
      fieldname === 'coupon'
        ? event.target.value.toUpperCase()
        : fieldname === 'tipcustom'
          ? event.target.value
              .replace(/[^\d.]/g, '')
              .replace(/^(\d*\.)(.*)\./, '$1$2')
              .replace(/^(\d*\.\d*)(.*)\./, '$1$2')
          : fieldname === 'storeCredit'
            ? event.target.value
                // Remove extra dots and ensure only one dot exists at most
                .replace(/[^\d.]/g, '') // Allow digits and dots only
                .replace(/^(\d*\.)(.*)\./, '$1$2') // Remove extra dots
                .replace(/^(\d*\.\d*)(.*)\./, '$1$2') // Remove extra dots after the decimal point
            : event.target.value;

    const newCursorPosition = event.target.selectionStart;

    switch (fieldname) {
      case 'storeCredit':
        cursorposition.storeCredit = newCursorPosition;
        // --------------------------------------===
        let inputStr = fieldValue.replace(/\D/g, '');
        inputStr = inputStr.replace(/^0+/, '');

        if (inputStr.length === 0) {
          // fieldValue = "0.00";
        } else if (inputStr.length === 1) {
          fieldValue = '0.0' + inputStr;
        } else if (inputStr.length === 2) {
          fieldValue = '0.' + inputStr;
        } else {
          fieldValue =
            inputStr.slice(0, inputStr.length - 2) + '.' + inputStr.slice(-2);
        }
        if (
          parseFloat(fieldValue) > parseFloat(grandtotal - mytipamt).toFixed(2)
        ) {
          errors.storeCredit = `Store Credit can't be greater than $${parseFloat(
            grandtotal - mytipamt,
          ).toFixed(2)}`;
        } else if (fieldValue === '') {
          errors.storeCredit = '';
        } else if (parseFloat(fieldValue) > storecredit) {
          errors.storeCredit = "Credit can't be greater than your Store Credit";
        } else if (parseFloat(fieldValue) === 0) {
          errors.storeCredit = "Credit can't be 0.";
        } else {
          errors.storeCredit = '';
        }
        break;
      default:
        break;
    }

    setValues((previousState) => ({
      ...previousState,
      errors,
      [fieldname]: fieldValue,
    }));
    if (fieldValue === '') {
      setValues((previousState) => ({
        ...previousState,
        errors: {
          loyaltyPoint: '',
        },
      }));
    }
  };

  const handleInputLoyaltyPoint = (
    event,
    subtotal,
    loyaltyPoint,
    couponDiscount,
    setCouponValue,
  ) => {
    event.preventDefault();

    const { errors, cursorposition } = values;

    const fieldname = event.target.name;
    let fieldValue =
      fieldname === 'loyaltyPoint'
        ? event.target.value
            // Remove extra dots and ensure only one dot exists at most
            .replace(/[^\d.]/g, '') // Allow digits and dots only
            .replace(/^(\d*\.)(.*)\./, '$1$2') // Remove extra dots
            .replace(/^(\d*\.\d*)(.*)\./, '$1$2') // Remove extra dots after the decimal point
        : event.target.value;

    const newCursorPosition = event.target.selectionStart;

    switch (fieldname) {
      case 'loyaltyPoint':
        cursorposition.loyaltyPoint = newCursorPosition;
        let inputStr = fieldValue.replace(/\D/g, '');
        inputStr = inputStr.replace(/^0+/, '');

        if (inputStr.length === 0) {
          // fieldValue = "0.00";
        } else if (inputStr.length === 1) {
          fieldValue = '0.0' + inputStr;
        } else if (inputStr.length === 2) {
          fieldValue = '0.' + inputStr;
        } else {
          fieldValue =
            inputStr.slice(0, inputStr.length - 2) + '.' + inputStr.slice(-2);
        }

        // const afterProductCategoryDiscounts = handleProductCategoryDiscounts(
        //   cartData,
        //   productCategoryDiscounts,
        // );
        // const afterBogoCart = handleBogoDeals(
        //   afterProductCategoryDiscounts,
        //   bogoList.bogoList,
        // );
        // const afterMixAndMatchCart = handleMixAndMatchDeals(
        //   afterBogoCart,
        //   mixAndMatchList.mixAndMatchList,
        // );

        // since cart already has discounts

        const newSubTotal = getCouponAdjustedLoyaltyBase(
          percentageDeducCoupon,
        );

        const total =
          parseFloat(newSubTotal) > 0 ? parseFloat(newSubTotal).toFixed(2) : 0;
        const maximumLoyaltyAmount = getMaximumSafeLoyaltyAmount(
          parseFloat(total) || 0,
          couponDiscount,
          percentageDeducCoupon,
        );

        const eligibleToApplyLP = cartData.some((item) =>
          isProductEligibleForLoyaltyPoints(item, storeDataState),
        );

        if (!taxPricingReady) {
          errors.loyaltyPoint =
            'Unable to verify taxes right now. Please try again.';
        } else if (!eligibleToApplyLP) {
          errors.disableLoyaltyPoint = true;

          setTimeout(() => {
            setValues((prev) => ({
              ...prev,
              loyaltyPoint: '',
            }));
          }, 100);
        } else if (parseFloat(fieldValue) > maximumLoyaltyAmount) {
          errors.loyaltyPoint = `Loyalty Points can't be greater than $${parseFloat(
            maximumLoyaltyAmount,
          ).toFixed(2)}`;
        } else if (fieldValue === '') {
          errors.loyaltyPoint = '';
        } else if (parseFloat(fieldValue) > loyaltyPoint) {
          errors.loyaltyPoint =
            "Points can't be greater than your loyalty points.";
        } else if (parseFloat(fieldValue) === 0) {
          errors.loyaltyPoint = "Points can't be 0.";
        } else {
          errors.loyaltyPoint = '';
        }
        break;
      default:
        break;
    }

    setValues((previousState) => ({
      ...previousState,
      errors,
      [fieldname]: fieldValue,
    }));
    if (fieldValue === '') {
      setValues((previousState) => ({
        ...previousState,
        errors: {
          coupon: '',
        },
      }));
      setCouponValue(true);
    }
  };

  // handleRemoveStoreCredit
  const handleUseStoreCredit = () => {
    const requestedAmount = parseFloat(values.storeCredit);
    const availableCreditValue = parseFloat(
      lp_sc_user_data?.customer_store_credit ?? 0,
    );
    const availableCredit = Number.isFinite(availableCreditValue)
      ? availableCreditValue
      : 0;
    const currentPayableLimit = getStoreCreditLimitAfterLoyalty(
      values.usedLoyaltyPoint,
      amountDeducCoupon,
      percentageDeducCoupon,
    );

    if (
      !taxPricingReady ||
      !Number.isFinite(requestedAmount) ||
      requestedAmount <= 0 ||
      requestedAmount > availableCredit ||
      requestedAmount > currentPayableLimit
    ) {
      setValues((previousState) => ({
        ...previousState,
        usedStoreCredit: '',
        errors: {
          ...previousState.errors,
          storeCredit:
            !taxPricingReady
              ? 'Unable to verify taxes right now. Please try again.'
              : currentPayableLimit > 0
              ? `Store Credit can't be greater than $${Math.min(
                  availableCredit,
                  currentPayableLimit,
                ).toFixed(2)}`
              : 'Store Credit cannot be applied to an empty cart.',
        },
      }));
      persistAdjustments({ storeCreditAmount: 0 });
      return false;
    }

    const loyaltyPointsNotAdded =
      (parseFloat(values.usedLoyaltyPoint) <= 0 ||
        values.usedLoyaltyPoint === '') &&
      parseFloat(values.loyaltyPoint) > 0;

    setValues((previousState) => ({
      ...previousState,
      loyaltyPoint: loyaltyPointsNotAdded ? '' : previousState.loyaltyPoint,
      usedStoreCredit: values.storeCredit,
      errors: {
        ...previousState.errors,
        storeCredit: '',
        loyaltyPoint: loyaltyPointsNotAdded
          ? ''
          : previousState.errors.loyaltyPoint,
      },
    }));
    persistAdjustments({ storeCreditAmount: requestedAmount });
    // setdiscountAmt(discountAmt + parseFloat(values.storeCredit));
    return true;
  };
  useEffect(() => {
    if (
      cartData &&
      cartData.length > 0 &&
      values.loyaltyPoint &&
      values.usedLoyaltyPoint
    ) {
      const setLoyaltyPoint = () => {
        // const afterProductCategoryDiscounts = handleProductCategoryDiscounts(
        //   cartData,
        //   productCategoryDiscounts,
        // );
        // const afterBogoCart = handleBogoDeals(
        //   afterProductCategoryDiscounts,
        //   bogoList.bogoList,
        // );
        // const afterMixAndMatchCart = handleMixAndMatchDeals(
        //   afterBogoCart,
        //   mixAndMatchList.mixAndMatchList,
        // );

        const usePointProductsTotal = getCouponAdjustedLoyaltyBase(
          percentageDeducCoupon,
        );

        // totalPrice
        // const percentageDeducted = parseFloat(
        //   (values.loyaltyPoint / parseFloat(usePointProductsTotal)) * 100,
        // ).toFixed(2);


        const percentageDeducted = parseFloat(
          (values.loyaltyPoint / parseFloat(usePointProductsTotal)) * 100,
        );


        setpercentageDeducLoy(percentageDeducted);
      };

      setLoyaltyPoint();
    }
  }, [
    bogoList.bogoList,
    mixAndMatchList.mixAndMatchList,
    productCategoryDiscounts,
    values.loyaltyPoint,
    cartData,
    percentageDeducCoupon,
    storeDataState,
    values.usedLoyaltyPoint,
  ]);

  useEffect(() => {
    const appliedAmount = parseFloat(values.usedLoyaltyPoint);
    if (!Number.isFinite(appliedAmount) || appliedAmount <= 0) return;

    const hasReadyOrderSummary =
      orderSummaryData &&
      Number.isFinite(parseFloat(orderSummaryData.cartProductSum)) &&
      orderSummaryData.singleStoreData &&
      typeof orderSummaryData.taxData === 'object' &&
      orderSummaryData.cartRevision === cartRevision &&
      orderSummaryData.pricingRevision === pricingRevision;
    if (!hasReadyOrderSummary || taxDetailState?.loading) return;

    const eligibleTotal = getCouponAdjustedLoyaltyBase(
      percentageDeducCoupon,
    );
    const availableAmount = parseFloat(
      lp_sc_user_data?.customer_lp_point?.cus_loyality_points_amount ?? 0,
    );
    const maximumLoyaltyAmount = taxPricingReady
      ? getMaximumSafeLoyaltyAmount(
          Math.min(eligibleTotal, availableAmount || 0),
          amountDeducCoupon,
          percentageDeducCoupon,
        )
      : 0;

    if (
      taxPricingReady &&
      Number.isFinite(availableAmount) &&
      appliedAmount <= maximumLoyaltyAmount
    ) {
      return;
    }

    loyaltyRequestRef.current += 1;
    setLoyaltyRequestPending(false);
    setpercentageDeducLoy(0);
    setusedLoyaltyPointApiUserData('');
    setdiscountAmt(0);
    setValues((previousState) => ({
      ...previousState,
      loyaltyPoint: '',
      usedLoyaltyPoint: '',
      errors: {
        ...previousState.errors,
        loyaltyPoint:
          'Your cart changed. Please review and apply loyalty points again.',
      },
    }));
    persistAdjustments({ loyaltyAmount: 0 });
  }, [
    cartData,
    cartRevision,
    lp_sc_user_data,
    amountDeducCoupon,
    checkoutDeliveryCharge,
    orderSummaryData,
    persistAdjustments,
    percentageDeducCoupon,
    pricingRevision,
    storeDataState,
    taxDetailData,
    taxDetailState?.loading,
    taxPricingReady,
    values.usedLoyaltyPoint,
  ]);

  useEffect(() => {
    const appliedAmount = parseFloat(values.usedStoreCredit);
    if (!Number.isFinite(appliedAmount) || appliedAmount <= 0) return;

    const hasReadyOrderSummary =
      orderSummaryData &&
      Number.isFinite(parseFloat(orderSummaryData.cartProductSum)) &&
      orderSummaryData.singleStoreData &&
      typeof orderSummaryData.taxData === 'object' &&
      orderSummaryData.cartRevision === cartRevision &&
      orderSummaryData.pricingRevision === pricingRevision;
    if (!hasReadyOrderSummary) return;

    const appliedLoyaltyAmount = parseFloat(values.usedLoyaltyPoint);
    if (Number.isFinite(appliedLoyaltyAmount) && appliedLoyaltyAmount > 0) {
      const eligibleLoyaltyTotal = getCouponAdjustedLoyaltyBase(
        percentageDeducCoupon,
      );
      const availableLoyaltyAmount = parseFloat(
        lp_sc_user_data?.customer_lp_point?.cus_loyality_points_amount ?? 0,
      );
      const maximumLoyaltyAmount = getMaximumSafeLoyaltyAmount(
        Math.min(eligibleLoyaltyTotal, availableLoyaltyAmount || 0),
        amountDeducCoupon,
        percentageDeducCoupon,
      );

      if (
        !Number.isFinite(availableLoyaltyAmount) ||
        appliedLoyaltyAmount > maximumLoyaltyAmount
      ) {
        return;
      }
    }

    const availableCreditValue = parseFloat(
      lp_sc_user_data?.customer_store_credit ?? 0,
    );
    const availableCredit = Number.isFinite(availableCreditValue)
      ? availableCreditValue
      : 0;
    const currentPayableLimit = getStoreCreditLimitAfterLoyalty(
      values.usedLoyaltyPoint,
      amountDeducCoupon,
      percentageDeducCoupon,
    );

    if (
      taxPricingReady &&
      appliedAmount <= availableCredit &&
      appliedAmount <= currentPayableLimit
    ) {
      return;
    }

    setValues((previousState) => ({
      ...previousState,
      storeCredit: '',
      usedStoreCredit: '',
      errors: {
        ...previousState.errors,
        storeCredit:
          'Your cart changed. Please review and apply store credit again.',
      },
    }));
    persistAdjustments({ storeCreditAmount: 0 });
  }, [
    cartData,
    cartRevision,
    lp_sc_user_data,
    amountDeducCoupon,
    checkoutDeliveryCharge,
    orderSummaryData,
    percentageDeducCoupon,
    persistAdjustments,
    pricingRevision,
    storeDataState,
    taxDetailData,
    taxPricingReady,
    values.usedLoyaltyPoint,
    values.usedStoreCredit,
  ]);

  const handleUseLoyaltyPoint = async (
    _event,
    requestedAmount = values.loyaltyPoint,
    options = {},
  ) => {
    const shouldPersist = options.persist !== false;
    const refreshExpiry = options.refreshExpiry !== false;
    const normalizedRequestedAmount = parseFloat(requestedAmount);
    const availableLoyaltyAmount = parseFloat(
      lp_sc_user_data?.customer_lp_point?.cus_loyality_points_amount ?? 0,
    );
    const appliedStoreCreditOverride = parseFloat(
      options.storeCreditAmountOverride,
    );
    const eligibleToApplyLP = cartData.some((item) =>
      isProductEligibleForLoyaltyPoints(item, storeDataState),
    );

    if (!taxPricingReady) {
      setValues((previousState) => ({
        ...previousState,
        loyaltyPoint: '',
        errors: {
          ...previousState.errors,
          loyaltyPoint:
            'Unable to verify taxes right now. Please try again.',
        },
      }));
      persistAdjustments(
        { loyaltyAmount: 0 },
        { refreshExpiry },
      );
      return false;
    }

    // if all products are not eligible for using LP
    if (!eligibleToApplyLP) {
      setValues((prev) => ({
        ...prev,
        loyaltyPoint: '',
        errors: {
          ...prev.errors,
          disableLoyaltyPoint: true,
        },
      }));
      persistAdjustments(
        { loyaltyAmount: 0 },
        { refreshExpiry },
      );
      return false;
    }
    const overridePercentage = parseFloat(options.couponPercentageOverride);
    const couponPercentage = Number.isFinite(overridePercentage)
      ? overridePercentage
      : parseFloat(percentageDeducCoupon) || 0;
    const overrideCouponDiscount = parseFloat(options.couponDiscountOverride);
    const couponDiscount = Number.isFinite(overrideCouponDiscount)
      ? overrideCouponDiscount
      : parseFloat(amountDeducCoupon) || 0;
    const newSubTotal = getCouponAdjustedLoyaltyBase(couponPercentage);

    const eligibleLoyaltyTotal =
      parseFloat(newSubTotal) > 0 ? parseFloat(newSubTotal) : 0;
    const maximumLoyaltyAmount = getMaximumSafeLoyaltyAmount(
      Math.min(eligibleLoyaltyTotal, availableLoyaltyAmount || 0),
      couponDiscount,
      couponPercentage,
    );

    // if user applied LP is more than valid then don't apply
    if (
      !Number.isFinite(normalizedRequestedAmount) ||
      normalizedRequestedAmount <= 0 ||
      !Number.isFinite(availableLoyaltyAmount) ||
      normalizedRequestedAmount > availableLoyaltyAmount ||
      normalizedRequestedAmount > maximumLoyaltyAmount
    ) {
      setValues((prev) => ({
        ...prev,
        loyaltyPoint: '',
        errors: {
          ...prev.errors,
          // disableLoyaltyPoint: true,
          loyaltyPoint: `Loyalty Points can't be greater than $${parseFloat(
            maximumLoyaltyAmount,
          ).toFixed(2)}`,
        },
      }));
      persistAdjustments(
        { loyaltyAmount: 0 },
        { refreshExpiry },
      );
      return false;
    }

    const requestId = ++loyaltyRequestRef.current;
    const requestDraftKey = currentDraftKeyRef.current;
    const requestGroupId = currentGroupIdRef.current;
    const requestIsCurrent = () =>
      providerActiveRef.current &&
      requestId === loyaltyRequestRef.current &&
      requestDraftKey === currentDraftKeyRef.current &&
      requestGroupId === currentGroupIdRef.current;

    setLoyaltyRequestPending(true);
    try {
      const data = {
        merchant_id: merchantId,
        customer_id: AuthUserState?.id,
        used_loyality_amt: normalizedRequestedAmount,
        group_id: requestGroupId,
      };
      const response = await axios.post(BASE_URL_SANDBOX + USED_LP_POINTS, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (!requestIsCurrent()) return false;

      if (response.data.status === 200) {
        const usedAmount = parseFloat(
          response.data?.used_amt ?? normalizedRequestedAmount,
        );
        const appliedAmount = Number.isFinite(usedAmount)
          ? usedAmount.toFixed(2)
          : normalizedRequestedAmount.toFixed(2);
        const latestMaximumLoyaltyAmount = getMaximumSafeLoyaltyAmount(
          Math.min(eligibleLoyaltyTotal, availableLoyaltyAmount || 0),
          couponDiscount,
          couponPercentage,
        );

        if (parseFloat(appliedAmount) > latestMaximumLoyaltyAmount) {
          setValues((previousState) => ({
            ...previousState,
            loyaltyPoint: '',
            usedLoyaltyPoint: '',
            errors: {
              ...previousState.errors,
              loyaltyPoint: `Loyalty Points can't be greater than $${latestMaximumLoyaltyAmount.toFixed(
                2,
              )}`,
            },
          }));
          setusedLoyaltyPointApiUserData('');
          setpercentageDeducLoy(0);
          setdiscountAmt(0);
          persistAdjustments(
            { loyaltyAmount: 0 },
            { refreshExpiry },
          );
          return false;
        }

        const latestStoreCredit = Number.isFinite(appliedStoreCreditOverride)
          ? appliedStoreCreditOverride
          : parseFloat(valuesRef.current.usedStoreCredit) || 0;
        const availableStoreCredit = Math.max(
          0,
          parseFloat(lp_sc_user_data?.customer_store_credit) || 0,
        );
        const storeCreditLimit = getStoreCreditLimitAfterLoyalty(
          appliedAmount,
          couponDiscount,
          couponPercentage,
        );
        const reconciledStoreCredit = Math.min(
          latestStoreCredit,
          availableStoreCredit,
          storeCreditLimit,
        );
        const reconciledStoreCreditValue =
          reconciledStoreCredit > 0
            ? reconciledStoreCredit.toFixed(2)
            : '';

        setusedLoyaltyPointApiUserData(response.data);
        setValues((previousState) => ({
          ...previousState,
          loyaltyPoint: appliedAmount,
          usedLoyaltyPoint: appliedAmount,
          storeCredit: reconciledStoreCreditValue,
          usedStoreCredit: reconciledStoreCreditValue,
          errors: {
            ...previousState.errors,
            loyaltyPoint: '',
            storeCredit: '',
          },
        }));
        setdiscountAmt(parseFloat(appliedAmount));
        if (shouldPersist) {
          persistAdjustments({
            loyaltyAmount: parseFloat(appliedAmount),
            storeCreditAmount: reconciledStoreCredit,
          });
        }
        return parseFloat(appliedAmount);
      } else {
        setValues((previousState) => ({
          ...previousState,
          usedLoyaltyPoint: '',
          errors: {
            ...previousState.errors,
            loyaltyPoint:
              response.data?.message ||
              'Unable to apply loyalty points. Please try again.',
          },
        }));
        setusedLoyaltyPointApiUserData('');
        setpercentageDeducLoy(0);
        setdiscountAmt(0);
        persistAdjustments(
          { loyaltyAmount: 0 },
          { refreshExpiry },
        );
        return false;
      }
    } catch {
      if (!requestIsCurrent()) return false;

      setValues((previousState) => ({
        ...previousState,
        usedLoyaltyPoint: '',
        errors: {
          ...previousState.errors,
          loyaltyPoint: 'Unable to apply loyalty points. Please try again.',
        },
      }));
      setusedLoyaltyPointApiUserData('');
      setpercentageDeducLoy(0);
      setdiscountAmt(0);
      if (shouldPersist) {
        persistAdjustments(
          { loyaltyAmount: 0 },
          { refreshExpiry },
        );
      }
      return false;
    } finally {
      if (requestIsCurrent()) {
        setLoyaltyRequestPending(false);
      }
    }
  };

  const handleUseLoyaltyPointRef = useRef(handleUseLoyaltyPoint);
  handleUseLoyaltyPointRef.current = handleUseLoyaltyPoint;

  const handleRemoveStoreCredit = (setCouponValue) => {
    // const loyaltyPointsNotAdded =
    //   (parseFloat(values.usedLoyaltyPoint) <= 0 ||
    //     values.usedLoyaltyPoint === '') &&
    //   parseFloat(values.loyaltyPoint) > 0;

    setValues((previousState) => ({
      ...previousState,
      usedStoreCredit: '',
      // loyaltyPoint: loyaltyPointsNotAdded ? '' : previousState.loyaltyPoint,
      storeCredit: '',
      errors: {
        ...previousState.errors,
        coupon: '',
        // loyaltyPoint: loyaltyPointsNotAdded
        //   ? ''
        //   : previousState.errors.loyaltyPoint,
      },
    }));

    if (values.coupon !== '') {
      setCouponValue(true);
    }

    if (values.errors.loyaltyPoint !== '') {
      setValues((previousState) => ({
        ...previousState,
        errors: { ...previousState.errors, loyaltyPoint: '' },
      }));
    }
    persistAdjustments({ storeCreditAmount: 0 });
  };

  const handleRemoveLoyaltyPoint = (setCouponValue) => {
    loyaltyRequestRef.current += 1;
    setLoyaltyRequestPending(false);
    setValues((previousState) => ({
      ...previousState,
      usedLoyaltyPoint: '',
      loyaltyPoint: '',
      errors: { ...previousState.errors, coupon: '' },
    }));
    if (values.coupon !== '') {
      setCouponValue(true);
    }

    setpercentageDeducLoy(0);
    setusedLoyaltyPointApiUserData('');
    setdiscountAmt(0);
    persistAdjustments({ loyaltyAmount: 0 });
  };

  const refreshCheckoutBalances = useCallback(() => {
    const customerId = String(AuthUserState?.id || '').trim();
    const requestMerchantId = String(merchantId || '').trim();

    if (!customerId || !requestMerchantId) {
      return Promise.resolve(null);
    }

    return dispatch(
      fetchLpscPaymentData({
        merchant_id: requestMerchantId,
        customer_id: customerId,
        group_id: currentGroupIdRef.current,
      }),
    );
  }, [AuthUserState?.id, dispatch, merchantId]);
  useEffect(() => {
    if (checkoutDraftScope) {
      readCheckoutAdjustmentDraft(checkoutDraftScope);
    }

    restoreRunRef.current += 1;
    hydrationKeyRef.current = '';
    inFlightHydrationKeyRef.current = '';
    couponRequestGenerationRef.current += 1;
    loyaltyRequestRef.current += 1;
    setCouponRequestPending(false);
    setLoyaltyRequestPending(false);
    setIsRestoringAdjustments(false);
    setAdjustmentsHydrated(false);
    setAppliedTip({ mode: 'none', value: 0 });
    dispatch(removeCoupon(''));
    clearDerivedCoupon();
    setpercentageDeducLoy(0);
    setusedLoyaltyPointApiUserData('');
    setdiscountAmt(0);
    setValues((previousState) => ({
      ...previousState,
      coupon: '',
      loyaltyPoint: '',
      tip: '',
      tipcustom: '',
      storeCredit: '',
      usedLoyaltyPoint: '',
      usedStoreCredit: '',
      tipRemoved: false,
      errors: {
        ...previousState.errors,
        coupon: '',
        loyaltyPoint: '',
        storeCredit: '',
        tip: '',
        tipcustom: '',
      },
    }));
  }, [
    checkoutDraftKey,
    checkoutDraftScope,
    clearDerivedCoupon,
    dispatch,
  ]);

  useEffect(() => {
    if (
      !checkoutDraftScope ||
      !checkoutDraftKey ||
      !Array.isArray(cartData) ||
      cartData.length === 0 ||
      derivedCartRevision !== cartRevision ||
      !Number.isFinite(parseFloat(totalPrice)) ||
      Math.abs(parseFloat(totalPrice) - derivedCartTotal) >= 0.005 ||
      hydrationKeyRef.current === checkoutDraftKey ||
      inFlightHydrationKeyRef.current === checkoutDraftKey
    ) {
      return;
    }

    const draft = readCheckoutAdjustmentDraft(checkoutDraftScope);
    if (!draft) {
      hydrationKeyRef.current = checkoutDraftKey;
      resetInMemoryCheckoutAdjustments();
      setAdjustmentsHydrated(true);
      dispatch(removeCoupon(''));
      clearDerivedCoupon();
      return;
    }

    const hasReadyOrderSummary =
      orderSummaryData &&
      Number.isFinite(parseFloat(orderSummaryData.cartProductSum)) &&
      orderSummaryData.singleStoreData &&
      typeof orderSummaryData.taxData === 'object' &&
      orderSummaryData.cartRevision === cartRevision &&
      orderSummaryData.pricingRevision === pricingRevision;

    if (
      (draft.loyaltyAmount > 0 || draft.storeCreditAmount > 0) &&
      (!hasReadyOrderSummary ||
        taxDetailState?.loading ||
        checkoutDeliveryCharge === null)
    ) {
      return;
    }

    const requiresCustomerBalance =
      draft.loyaltyAmount > 0 || draft.storeCreditAmount > 0;
    const balanceRequestFailed =
      requiresCustomerBalance &&
      ((Boolean(AuthUserState?.id) && Boolean(lpscPaymentState?.error)) ||
        !taxPricingReady);
    const hasCustomerBalanceResponse =
      Array.isArray(lp_sc_user_data) ||
      (lp_sc_user_data &&
        typeof lp_sc_user_data === 'object' &&
        ('customer_store_credit' in lp_sc_user_data ||
          'customer_lp_point' in lp_sc_user_data ||
          'loyalty_program' in lp_sc_user_data));

    if (
      requiresCustomerBalance &&
      AuthUserState?.id &&
      (lpscPaymentState?.loading ||
        (!hasCustomerBalanceResponse && !lpscPaymentState?.error))
    ) {
      return;
    }

    const restoreRunId = ++restoreRunRef.current;
    const restoreDraftKey = checkoutDraftKey;
    inFlightHydrationKeyRef.current = restoreDraftKey;
    loyaltyRequestRef.current += 1;
    setLoyaltyRequestPending(false);
    dispatch(removeCoupon(''));
    clearDerivedCoupon();
    resetInMemoryCheckoutAdjustments();
    setIsRestoringAdjustments(true);

    const restore = async () => {
      try {
        const restoredTip = draft.tip || { mode: 'none', value: 0 };
        setAppliedTip(restoredTip);
        setValues((previousState) => ({
          ...previousState,
          tip:
            restoredTip.mode === 'percent' ? String(restoredTip.value) : '',
          tipcustom:
            restoredTip.mode === 'custom'
              ? Number(restoredTip.value).toFixed(2)
              : '',
          tipRemoved: isCheckoutTipExplicitlyRemoved(draft),
          errors: {
            ...previousState.errors,
            coupon: '',
            loyaltyPoint:
              requiresCustomerBalance && !taxPricingReady
                ? 'Unable to verify taxes. Please apply your balance again.'
                : '',
            storeCredit:
              requiresCustomerBalance && !taxPricingReady
                ? 'Unable to verify taxes. Please apply your balance again.'
                : '',
            tip: '',
            tipcustom: '',
          },
        }));

        const restoredCoupon = draft.couponCode
          ? await requestCoupon(draft.couponCode, {
              persist: false,
              refreshExpiry: false,
            })
          : null;

        if (balanceRequestFailed) {
          persistAdjustments(
            { loyaltyAmount: 0, storeCreditAmount: 0 },
            { refreshExpiry: false },
          );
        }

        if (
          restoreRunId !== restoreRunRef.current ||
          restoreDraftKey !== currentDraftKeyRef.current
        ) {
          return;
        }

        const eligibleCouponBase = couponEligibleSubtotal(cartData);
        const restoredCouponDiscount = restoredCoupon
          ? calculateCouponDiscount(restoredCoupon, eligibleCouponBase)
          : 0;
        const restoredCouponPercentage = restoredCoupon
          ? couponDeductionPercentage(restoredCoupon, eligibleCouponBase)
          : 0;

        let restoredLoyaltyAmount = 0;
        if (draft.loyaltyAmount > 0 && !balanceRequestFailed) {
          if (AuthUserState?.id) {
            setValues((previousState) => ({
              ...previousState,
              loyaltyPoint: Number(draft.loyaltyAmount).toFixed(2),
            }));
            const loyaltyRestored = await handleUseLoyaltyPointRef.current(
              null,
              draft.loyaltyAmount,
              {
                persist: false,
                refreshExpiry: false,
                couponPercentageOverride: restoredCouponPercentage,
                couponDiscountOverride: restoredCouponDiscount,
                storeCreditAmountOverride: 0,
              },
            );
            const acceptedLoyaltyAmount = parseFloat(loyaltyRestored);
            if (
              Number.isFinite(acceptedLoyaltyAmount) &&
              acceptedLoyaltyAmount > 0
            ) {
              restoredLoyaltyAmount = acceptedLoyaltyAmount;
            }
          } else {
            persistAdjustments(
              { loyaltyAmount: 0 },
              { refreshExpiry: false },
            );
          }
        }

        if (
          restoreRunId !== restoreRunRef.current ||
          restoreDraftKey !== currentDraftKeyRef.current
        ) {
          return;
        }

        if (draft.storeCreditAmount > 0 && !balanceRequestFailed) {
          const availableCreditValue = parseFloat(
            lp_sc_user_data?.customer_store_credit ?? 0,
          );
          const availableCredit = Number.isFinite(availableCreditValue)
            ? availableCreditValue
            : 0;
          const currentPayableLimit = getStoreCreditLimitAfterLoyalty(
            restoredLoyaltyAmount,
            restoredCouponDiscount,
            restoredCouponPercentage,
          );

          if (
            AuthUserState?.id &&
            draft.storeCreditAmount <= availableCredit &&
            draft.storeCreditAmount <= currentPayableLimit
          ) {
            const restoredAmount = Number(draft.storeCreditAmount).toFixed(2);
            setValues((previousState) => ({
              ...previousState,
              storeCredit: restoredAmount,
              usedStoreCredit: restoredAmount,
            }));
          } else {
            persistAdjustments(
              { storeCreditAmount: 0 },
              { refreshExpiry: false },
            );
          }
        }

      } finally {
        if (
          restoreRunId === restoreRunRef.current &&
          restoreDraftKey === currentDraftKeyRef.current
        ) {
          hydrationKeyRef.current = restoreDraftKey;
          inFlightHydrationKeyRef.current = '';
          setIsRestoringAdjustments(false);
          setAdjustmentsHydrated(true);
        }
      }
    };

    void restore().catch(() => {});
  }, [
    AuthUserState?.id,
    cartData,
    cartRevision,
    checkoutDeliveryCharge,
    checkoutDraftKey,
    checkoutDraftScope,
    clearDerivedCoupon,
    derivedCartRevision,
    derivedCartTotal,
    dispatch,
    lp_sc_user_data,
    lpscPaymentState?.error,
    lpscPaymentState?.loading,
    orderSummaryData,
    persistAdjustments,
    pricingRevision,
    requestCoupon,
    resetInMemoryCheckoutAdjustments,
    taxDetailData,
    taxDetailState?.loading,
    taxPricingReady,
    totalPrice,
  ]);

  useEffect(() => {
    if (
      !checkoutDraftKey ||
      !merchantId ||
      !Array.isArray(cartDataState) ||
      cartDataState.length > 0
    ) {
      return;
    }

    let storedCart = [];
    try {
      const parsedCart = JSON.parse(
        localStorage.getItem(`cart_${merchantId}`),
      );
      storedCart = Array.isArray(parsedCart) ? parsedCart : [];
    } catch {
      storedCart = [];
    }
    if (storedCart.length > 0) return;

    restoreRunRef.current += 1;
    couponRequestGenerationRef.current += 1;
    loyaltyRequestRef.current += 1;
    hydrationKeyRef.current = checkoutDraftKey;
    inFlightHydrationKeyRef.current = '';
    clearCheckoutAdjustmentDraft();
    dispatch(removeCoupon(''));
    clearDerivedCoupon();
    setpercentageDeducLoy(0);
    setusedLoyaltyPointApiUserData('');
    setdiscountAmt(0);
    setAppliedTip({ mode: 'none', value: 0 });
    setIsRestoringAdjustments(false);
    setCouponRequestPending(false);
    setLoyaltyRequestPending(false);
    setAdjustmentsHydrated(true);
    setValues((previousState) => ({
      ...previousState,
      coupon: '',
      loyaltyPoint: '',
      tip: '',
      tipcustom: '',
      storeCredit: '',
      usedLoyaltyPoint: '',
      usedStoreCredit: '',
      tipRemoved: true,
      errors: {
        ...previousState.errors,
        coupon: '',
        loyaltyPoint: '',
        storeCredit: '',
        tip: '',
        tipcustom: '',
      },
    }));
  }, [
    cartDataState,
    checkoutDraftKey,
    clearDerivedCoupon,
    dispatch,
    merchantId,
  ]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleApplyCoupon(e);
    }
  };
  const checkoutPricingValidation = validateCheckoutBalanceAllocation();

  return {
    handleInputChange,
    values,
    setValues,
    handleAddingTip,
    handleApplyCoupon,
    requestCoupon,
    couponData,
    handleRemoveCoupon,
    handleKeyDown,
    orderSummaryData,
    handleStoreCredit,
    handleUseStoreCredit,
    handleRemoveStoreCredit,
    handleInputLoyaltyPoint,
    handleUseLoyaltyPoint,
    handleRemoveLoyaltyPoint,
    refreshCheckoutBalances,
    persistCustomTip,
    clearPersistedTip,
    clearTipAdjustment,
    appliedTip,
    adjustmentsHydrated,
    clearCheckoutAdjustments: clearCheckoutAdjustmentDraft,
    isRestoringAdjustments:
      !adjustmentsHydrated ||
      isRestoringAdjustments ||
      couponRequestPending ||
      loyaltyRequestPending,
    setCheckoutDeliveryCharge,
    validateCheckoutBalanceAllocation,
    checkoutPricingReady:
      checkoutPricingValidation.ready && checkoutPricingValidation.valid,
    amountDeducCoupon,
    discountAmt,
    baseCartData,
    // mKonnekt quote request for the current cart (null when dezi is off / no
    // rcn). buildPlaceOrderData keys the slice on it to attach the quote.
    mkonnektDiscountRequestBody: expectedMkonnektRequestBody,
  };
};

export default CouponTipSectionLogic;
